import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import api from "@/lib/api";
import { ArrowLeft, Pencil, Eraser, Trash2, Undo2, Square, DoorOpen, Menu, Send, RotateCcw, Type, X, ZoomIn, ZoomOut, Maximize2, Hand, HelpCircle } from "lucide-react";

const DRAFT_KEY = "planit-draw-draft";

// Draft helpers
const loadDraft = () => {
  try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || "null"); } catch { return null; }
};
const saveDraft = (patch) => {
  const cur = loadDraft() || {};
  const next = { ...cur, ...patch, ts: Date.now() };
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify(next)); } catch (e) { console.warn("draft save failed", e); }
};
const clearDraft = () => { try { localStorage.removeItem(DRAFT_KEY); } catch (e) { console.warn("draft clear failed", e); } };

// ---- Preview icons ----
function SketchPreview() {
  return (
    <svg viewBox="0 0 120 80" className="w-24 h-16 text-stone-800" xmlns="http://www.w3.org/2000/svg">
      <path d="M 12 62 Q 30 20, 60 30 T 108 20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M 22 68 Q 40 55, 70 62 T 100 55" fill="none" stroke="#D06D4D" strokeWidth="2" strokeLinecap="round" opacity="0.75" />
      <circle cx="88" cy="18" r="2.5" fill="#D06D4D" />
    </svg>
  );
}
function GuidedPreview() {
  return (
    <svg viewBox="0 0 120 80" className="w-24 h-16 text-stone-800" xmlns="http://www.w3.org/2000/svg">
      {/* grid */}
      <g stroke="#E7E5E4" strokeWidth="0.8">
        {[15, 30, 45, 60, 75, 90, 105].map((x) => <line key={"v" + x} x1={x} y1="6" x2={x} y2="74" />)}
        {[15, 30, 45, 60].map((y) => <line key={"h" + y} x1="6" y1={y} x2="114" y2={y} />)}
      </g>
      {/* walls */}
      <rect x="20" y="18" width="80" height="46" fill="none" stroke="currentColor" strokeWidth="3" />
      {/* door arc */}
      <path d="M 45 64 A 12 12 0 0 1 57 52" fill="none" stroke="#059669" strokeWidth="1.6" />
      {/* window */}
      <line x1="70" y1="18" x2="90" y2="18" stroke="#2563eb" strokeWidth="2.4" />
    </svg>
  );
}

// ---- Mode selector ----
function ModePicker({ onPick, projectName, setProjectName, hasDraft }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-6 py-14">
      <div className="max-w-4xl w-full">
        <button onClick={() => window.history.back()} data-testid="draw-close" className="text-sm text-stone-600 hover:text-stone-900 mb-8 inline-flex items-center gap-2">
          <ArrowLeft size={16} /> {t("draw.back")}
        </button>
        <div className="eyebrow">Studio Tool</div>
        <h1 className="font-serif text-5xl lg:text-6xl mt-4 text-stone-900" data-testid="draw-picker-title">{t("draw.title")}</h1>
        <div className="mt-4 text-lg text-stone-700 max-w-2xl draw-sub prose prose-stone" data-testid="draw-picker-sub">
          <Trans i18nKey="draw.sub" components={{ p: <p />, strong: <strong />, em: <em />, br: <br />, ul: <ul />, ol: <ol />, li: <li /> }} />
        </div>

        {/* Project name */}
        <div className="mt-10 max-w-xl">
          <label className="block text-xs uppercase tracking-widest text-stone-500 mb-2">Nome del progetto (opzionale)</label>
          <input
            data-testid="draw-project-name"
            type="text"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            placeholder="Es. Cucina di casa mia, Camera dei bimbi…"
            maxLength={80}
            className="w-full bg-white border border-stone-300 focus:border-[#D06D4D] outline-none rounded-lg px-4 py-3 text-stone-900"
          />
        </div>

        {hasDraft && (
          <div data-testid="draw-draft-hint" className="mt-6 inline-flex items-center gap-2 text-xs text-stone-600 bg-stone-100 border border-stone-200 rounded-full px-4 py-2">
            <RotateCcw size={14} className="text-[#D06D4D]" /> Hai una bozza salvata. Verrà ripristinata quando riapri lo stesso disegno.
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 gap-6 max-w-xl mx-auto">
          <button
            data-testid="draw-mode-sketch"
            onClick={() => onPick("sketch")}
            className="text-left rounded-2xl border border-stone-300 hover:border-stone-900 bg-white p-8 transition-all shadow-[8px_8px_24px_rgba(28,25,23,0.06)] hover:shadow-[10px_10px_32px_rgba(28,25,23,0.12)] group"
          >
            <div className="flex items-center gap-4">
              <SketchPreview />
              <Pencil className="text-[#D06D4D]" size={22} />
            </div>
            <h3 className="font-serif text-2xl mt-6 text-stone-900">{t("draw.modeSketch")}</h3>
            <p className="mt-3 text-stone-600">{t("draw.modeSketchDesc")}</p>
            <div className="mt-5 space-y-3 text-sm text-stone-700 leading-relaxed">
              <p>
                <span className="font-semibold text-stone-900">Libertà totale sul foglio</span> per inserire porte, finestre e dettagli come preferisci.
              </p>
              <ul className="space-y-2">
                <li className="flex gap-2">
                  <span className="text-[#D06D4D] mt-1">•</span>
                  <span><span className="font-semibold text-stone-900">Come funziona:</span> Ti muovi senza vincoli. Tracci le pareti e posizioni gli elementi dove vuoi tu, muovendo il mouse o il dito in totale libertà. Puoi aggiungere note e misure utilizzando il tasto T.</span>
                </li>
              </ul>
            </div>
            <div className="mt-6 text-sm font-semibold text-[#D06D4D] group-hover:underline">{t("draw.start")} →</div>
          </button>
          {/* GUIDED MODE — temporarily hidden from UI (2026-02-08). Backend + GuidedCanvas component
              (line ~505) and DXF export in server.py are preserved for future re-activation.
          <button data-testid="draw-mode-guided" onClick={() => onPick("guided")} ...>...</button>
          */}
        </div>
      </div>
    </div>
  );
}

// ---- Sketch mode ----
function SketchCanvas({ onSubmit, projectName }) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const [tool, setTool] = useState("pencil");
  const [color, setColor] = useState("#1C1917");
  const drawing = useRef(false);
  const strokes = useRef([]);
  const [step, setStep] = useState(0);
  const [texts, setTexts] = useState([]);
  const dragRef = useRef(null);
  const [zoomSketch, setZoomSketch] = useState(1);
  const [panSketch, setPanSketch] = useState({ x: 0, y: 0 });
  const pinchRef = useRef(null);
  const mousePanRef = useRef({ dragging: false, startX: 0, startY: 0, origX: 0, origY: 0 });
  const [interacting, setInteracting] = useState(false);
  const interactTimer = useRef(null);
  const markInteracting = () => {
    setInteracting(true);
    if (interactTimer.current) { clearTimeout(interactTimer.current); interactTimer.current = null; }
  };
  const clearInteracting = () => {
    if (interactTimer.current) clearTimeout(interactTimer.current);
    interactTimer.current = setTimeout(() => setInteracting(false), 700);
  };
  // Mobile guide: collapses to icon while drawing, auto-reveals after 10s idle
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);
  useEffect(() => {
    if (interacting) { setMobileGuideOpen(false); return; }
    const to = setTimeout(() => setMobileGuideOpen(true), 10000);
    return () => clearTimeout(to);
  }, [interacting]);

  // Zoom helpers (used by buttons & wheel)
  const doZoomAt = (factor, clientX, clientY) => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) return;
    const localX = clientX - rect.left;
    const localY = clientY - rect.top;
    setZoomSketch((z) => {
      const nz = Math.max(0.5, Math.min(3, z * factor));
      const k = nz / z;
      setPanSketch((p) => ({ x: localX - k * (localX - p.x), y: localY - k * (localY - p.y) }));
      return nz;
    });
  };
  const zoomFit = () => { setZoomSketch(1); setPanSketch({ x: 0, y: 0 }); };

  const paintBackground = (ctx, w, h) => { ctx.fillStyle = "#FBFAF7"; ctx.fillRect(0, 0, w, h); };
  const drawStroke = (ctx, s) => {
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.strokeStyle = s.tool === "eraser" ? "#FBFAF7" : s.color;
    ctx.lineWidth = s.tool === "eraser" ? 18 : 2.5;
    ctx.beginPath();
    s.points.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.stroke();
  };

  useEffect(() => {
    const c = canvasRef.current;
    c.width = c.offsetWidth * 2; c.height = c.offsetHeight * 2;
    const ctx = c.getContext("2d");
    ctx.scale(2, 2);
    paintBackground(ctx, c.width, c.height);
    // Restore draft
    const d = loadDraft();
    if (d && d.mode === "sketch" && Array.isArray(d.strokes) && d.strokes.length) {
      strokes.current = d.strokes;
      d.strokes.forEach((s) => drawStroke(ctx, s));
      toast.success("Bozza ripristinata");
    }
    if (d && d.mode === "sketch" && Array.isArray(d.texts)) {
      setTexts(d.texts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- canvas init + draft restore once on mount (refs/setters only)
  }, []);

  const persist = () => saveDraft({ mode: "sketch", projectName, strokes: strokes.current, texts });

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: ((t?.clientX ?? e.clientX) - rect.left) / zoomSketch, y: ((t?.clientY ?? e.clientY) - rect.top) / zoomSketch };
  };
  const start = (e) => {
    if (e.touches && e.touches.length >= 2) return; // pinch, skip drawing
    if (tool === "text") {
      const p = getPos(e);
      const id = Date.now() + Math.random();
      setTexts((prev) => {
        const next = [...prev, { id, x: p.x, y: p.y, content: "Testo" }];
        saveDraft({ mode: "sketch", projectName, strokes: strokes.current, texts: next });
        return next;
      });
      return;
    }
    drawing.current = true; const p = getPos(e); strokes.current.push({ tool, color, points: [p] });
    markInteracting();
  };
  const move = (e) => {
    if (!drawing.current) return;
    if (tool === "text") return;
    e.preventDefault();
    const p = getPos(e);
    const s = strokes.current[strokes.current.length - 1];
    s.points.push(p);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.strokeStyle = s.tool === "eraser" ? "#FBFAF7" : s.color;
    ctx.lineWidth = s.tool === "eraser" ? 18 : 2.5;
    const [a, b] = [s.points[s.points.length - 2], p];
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
  };
  const end = () => { if (drawing.current) { drawing.current = false; persist(); } clearInteracting(); };

  const redraw = () => {
    const c = canvasRef.current; const ctx = c.getContext("2d");
    paintBackground(ctx, c.width, c.height);
    strokes.current.forEach((s) => drawStroke(ctx, s));
  };
  const clearAll = () => { strokes.current = []; setTexts([]); redraw(); saveDraft({ mode: "sketch", projectName, strokes: [], texts: [] }); };
  const undo = () => { strokes.current.pop(); redraw(); persist(); };

  // Text drag handlers — pointer events cover both mouse & touch
  const startDrag = (e, id) => {
    e.stopPropagation();
    const rect = wrapperRef.current.getBoundingClientRect();
    const t = texts.find((tx) => tx.id === id);
    dragRef.current = { id, offsetX: e.clientX - rect.left - t.x, offsetY: e.clientY - rect.top - t.y };
    const onMove = (ev) => {
      if (!dragRef.current) return;
      ev.preventDefault();
      const r = wrapperRef.current.getBoundingClientRect();
      const nx = ev.clientX - r.left - dragRef.current.offsetX;
      const ny = ev.clientY - r.top - dragRef.current.offsetY;
      setTexts((prev) => prev.map((tx) => tx.id === dragRef.current.id ? { ...tx, x: nx, y: ny } : tx));
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setTimeout(() => { saveDraft({ mode: "sketch", projectName, strokes: strokes.current, texts }); }, 0);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };
  const updateText = (id, content) => {
    setTexts((prev) => {
      const next = prev.map((tx) => tx.id === id ? { ...tx, content } : tx);
      saveDraft({ mode: "sketch", projectName, strokes: strokes.current, texts: next });
      return next;
    });
  };
  const deleteText = (id) => {
    setTexts((prev) => {
      const next = prev.filter((tx) => tx.id !== id);
      saveDraft({ mode: "sketch", projectName, strokes: strokes.current, texts: next });
      return next;
    });
  };

  const done = () => {
    const c = canvasRef.current; const ctx = c.getContext("2d");
    // Bake texts onto canvas
    texts.forEach((tx) => {
      if (!tx.content) return;
      ctx.fillStyle = "#1C1917";
      ctx.font = "16px Manrope, system-ui, sans-serif";
      ctx.textBaseline = "top";
      ctx.fillText(tx.content, tx.x, tx.y);
    });
    const png = c.toDataURL("image/png");
    // Restore canvas without texts (so editing session stays clean)
    redraw();
    onSubmit({ mode: "sketch", png_data_url: png });
  };

  const guides = [t("draw.guide1"), t("draw.guide2"), t("draw.guide3")];

  return (
    <div className="min-h-screen bg-white relative">
      {/* Top bar */}
      <div className="absolute top-4 left-4 right-4 z-20 flex items-center justify-between gap-3">
        <button data-testid="draw-back" onClick={() => window.history.back()} className="canvas-glass px-4 py-2 rounded-full text-sm inline-flex items-center gap-2 text-stone-900">
          <ArrowLeft size={16} /> {t("draw.back")}
        </button>
        {projectName && (
          <div data-testid="draw-project-badge" className="canvas-glass px-4 py-2 rounded-full text-xs uppercase tracking-widest text-stone-700 truncate max-w-[40vw]">
            {projectName}
          </div>
        )}
        <button data-testid="draw-sketch-submit" onClick={done} className="bg-[#D06D4D] text-white px-6 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 hover:bg-[#b95a3d]">
          <Send size={14} /> {t("draw.form.send")}
        </button>
      </div>

      {/* Descriptive info above canvas — DESKTOP ONLY */}
      <div className="hidden lg:block absolute top-20 left-10 right-10 z-10 bg-white/85 backdrop-blur-sm border border-stone-200 rounded-xl px-6 py-4 shadow-[0_2px_8px_rgba(28,25,23,0.04)]">
        <div className="text-sm text-stone-700 leading-relaxed space-y-2">
          <p>
            <span className="font-semibold text-stone-900">Libertà totale sul foglio</span> per inserire porte, finestre e dettagli come preferisci.
          </p>
          <ul>
            <li className="flex gap-2">
              <span className="text-[#D06D4D] mt-1">•</span>
              <span><span className="font-semibold text-stone-900">Come funziona:</span> Ti muovi senza vincoli. Tracci le pareti e posizioni gli elementi dove vuoi tu, muovendo il mouse o il dito in totale libertà. Puoi aggiungere note e misure utilizzando il tasto T.</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Guide bubble — DESKTOP */}
      <div className="hidden lg:block absolute top-52 left-4 z-20 canvas-glass p-4 rounded-2xl max-w-xs pointer-events-none">
        <div className="eyebrow">Guida {step + 1}/3</div>
        <p className="mt-2 text-stone-800 text-sm">{guides[step]}</p>
        <div className="mt-3 flex gap-2 pointer-events-auto">
          {step > 0 && (
            <button data-testid="draw-guide-prev" onClick={() => setStep((s) => s - 1)} className="text-xs bg-stone-200 text-stone-900 px-3 py-1 rounded-full hover:bg-stone-300 transition-colors">←</button>
          )}
          <button data-testid="draw-guide-next" onClick={() => setStep((s) => (s + 1) % 3)} className="text-xs bg-stone-900 text-white px-3 py-1 rounded-full">→</button>
        </div>
      </div>

      {/* MOBILE: collapsible guide (icon → expanded card, auto-open after 10s idle) */}
      <div className="lg:hidden absolute top-16 left-3 z-30">
        {mobileGuideOpen ? (
          <div data-testid="mobile-guide-panel" className="canvas-glass p-3 rounded-2xl max-w-[260px] shadow-lg">
            <div className="flex items-start justify-between gap-2">
              <div className="eyebrow text-[10px]">Guida {step + 1}/3</div>
              <button data-testid="mobile-guide-close" onClick={() => setMobileGuideOpen(false)} className="text-stone-500 hover:text-stone-900 -mt-1"><X size={14} /></button>
            </div>
            <p className="mt-1 text-stone-800 text-xs leading-snug">{guides[step]}</p>
            <div className="mt-2 flex gap-1.5">
              {step > 0 && (
                <button data-testid="draw-guide-prev" onClick={() => setStep((s) => s - 1)} className="text-[11px] bg-stone-200 text-stone-900 px-2.5 py-0.5 rounded-full">←</button>
              )}
              <button data-testid="draw-guide-next" onClick={() => setStep((s) => (s + 1) % 3)} className="text-[11px] bg-stone-900 text-white px-2.5 py-0.5 rounded-full">→</button>
            </div>
          </div>
        ) : (
          <button data-testid="mobile-guide-icon" onClick={() => setMobileGuideOpen(true)} className="canvas-glass p-2 rounded-full text-stone-800 shadow hover:bg-stone-100 flex items-center gap-1.5 px-3">
            <HelpCircle size={16} /><span className="text-[11px] uppercase tracking-widest">Guida</span>
          </button>
        )}
      </div>

      {/* Tools */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 canvas-glass px-3 py-3 rounded-full flex items-center gap-2">
        {[
          { k: "pencil", i: <Pencil size={18} /> },
          { k: "eraser", i: <Eraser size={18} /> },
          { k: "text", i: <Type size={18} /> },
        ].map((b) => (
          <button
            key={b.k}
            data-testid={`sketch-tool-${b.k}`}
            onClick={() => setTool(b.k)}
            title={t(`draw.tools.${b.k}`)}
            className={`p-2 rounded-full transition-colors ${tool === b.k ? "bg-stone-900 text-white" : "hover:bg-stone-200 text-stone-800"}`}
          >{b.i}</button>
        ))}
        <input data-testid="sketch-color" type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-8 h-8 rounded-full border border-stone-300 cursor-pointer" />
        <div className="w-px h-6 bg-stone-300 mx-1" />
        <button data-testid="sketch-undo" onClick={undo} className="p-2 rounded-full hover:bg-stone-200 text-stone-800"><Undo2 size={18} /></button>
        <button data-testid="sketch-clear" onClick={clearAll} className="p-2 rounded-full hover:bg-stone-200 text-stone-800"><Trash2 size={18} /></button>
      </div>

      <div ref={wrapperRef} className="absolute inset-x-0 top-14 bottom-20 lg:inset-x-10 lg:top-48 lg:bottom-28 rounded-none lg:rounded-md border-0 lg:border lg:border-stone-300 overflow-hidden shadow-[inset_0_0_40px_rgba(28,25,23,0.10)] bg-[#FBFAF7]">
        <div className="absolute top-3 right-3 z-10 canvas-glass px-2 py-1 rounded-full flex items-center gap-1">
          <button data-testid="sketch-zoom-out" onClick={() => doZoomAt(1/1.2, (wrapperRef.current?.getBoundingClientRect().left||0)+50, (wrapperRef.current?.getBoundingClientRect().top||0)+50)} className="p-1.5 rounded-full hover:bg-stone-200 text-stone-800"><ZoomOut size={16} /></button>
          <button data-testid="sketch-zoom-in" onClick={() => doZoomAt(1.2, (wrapperRef.current?.getBoundingClientRect().left||0)+50, (wrapperRef.current?.getBoundingClientRect().top||0)+50)} className="p-1.5 rounded-full hover:bg-stone-200 text-stone-800"><ZoomIn size={16} /></button>
          <button data-testid="sketch-zoom-fit" onClick={zoomFit} className="p-1.5 rounded-full hover:bg-stone-200 text-stone-800"><Maximize2 size={16} /></button>
        </div>
        <canvas
          ref={canvasRef}
          data-testid="sketch-canvas"
          onMouseDown={(e) => {
            if (e.button === 1 || (e.button === 0 && e.altKey)) { e.preventDefault(); mousePanRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, origX: panSketch.x, origY: panSketch.y }; markInteracting(); return; }
            start(e);
          }}
          onMouseMove={(e) => {
            if (mousePanRef.current.dragging) { setPanSketch({ x: mousePanRef.current.origX + (e.clientX - mousePanRef.current.startX), y: mousePanRef.current.origY + (e.clientY - mousePanRef.current.startY) }); return; }
            move(e);
          }}
          onMouseUp={(e) => { if (mousePanRef.current.dragging) { mousePanRef.current.dragging = false; clearInteracting(); return; } end(); }}
          onMouseLeave={() => { mousePanRef.current.dragging = false; end(); }}
          onContextMenu={(e) => e.preventDefault()}
          onWheel={(e) => { e.preventDefault(); doZoomAt(e.deltaY < 0 ? 1.1 : 1/1.1, e.clientX, e.clientY); }}
          onTouchStart={(e) => {
            markInteracting();
            if (e.touches.length === 2) {
              e.preventDefault();
              const [a, b] = e.touches;
              pinchRef.current = { d: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY), z: zoomSketch, cx: (a.clientX + b.clientX) / 2, cy: (a.clientY + b.clientY) / 2, px: panSketch.x, py: panSketch.y };
              drawing.current = false;
            } else start(e);
          }}
          onTouchMove={(e) => {
            if (e.touches.length === 2 && pinchRef.current) {
              e.preventDefault();
              const [a, b] = e.touches;
              const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
              const cx = (a.clientX + b.clientX) / 2, cy = (a.clientY + b.clientY) / 2;
              const ratio = d / pinchRef.current.d;
              setZoomSketch(Math.max(0.5, Math.min(3, pinchRef.current.z * ratio)));
              setPanSketch({ x: pinchRef.current.px + (cx - pinchRef.current.cx), y: pinchRef.current.py + (cy - pinchRef.current.cy) });
            } else move(e);
          }}
          onTouchEnd={(e) => { if (e.touches.length < 2) pinchRef.current = null; end(); clearInteracting(); }}
          style={{ transform: `translate(${panSketch.x}px, ${panSketch.y}px) scale(${zoomSketch})`, transformOrigin: "0 0" }}
          className={`block w-full h-full touch-none ${tool === "text" ? "cursor-text" : "cursor-crosshair"}`}
        />
        {texts.map((tx) => (
          <div
            key={tx.id}
            data-testid={`sketch-text-${tx.id}`}
            style={{ left: tx.x, top: tx.y }}
            className="absolute group select-none"
          >
            <div
              onPointerDown={(e) => startDrag(e, tx.id)}
              className="flex items-start gap-1 bg-white/95 border border-stone-300 rounded shadow-sm px-1.5 py-0.5 cursor-move hover:border-[#D06D4D] touch-none"
            >
              <input
                type="text"
                value={tx.content}
                onChange={(e) => updateText(tx.id, e.target.value)}
                onPointerDown={(e) => e.stopPropagation()}
                className="bg-transparent outline-none text-sm text-stone-900 min-w-[60px]"
                style={{ width: `${Math.max(60, (tx.content?.length || 0) * 8 + 12)}px` }}
              />
              <button
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => deleteText(tx.id)}
                className="text-stone-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Elimina testo"
              ><X size={12} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Guided plan mode ----
const DEFAULT_CM_PX = 6;               // baseline pixels per cm
const CANVAS_PADDING = 60;             // px padding inside canvas for auto-fit
const DEFAULT_DOOR_W = 80;             // cm (standard interior door)
const DEFAULT_WINDOW_W = 100;          // cm
const clampCmPx = (v) => Math.max(2, Math.min(12, v));
// --- Wall geometry helpers (module-scoped for reuse) ---
const wallVec = (w) => {
  const dx = w.x2 - w.x1, dy = w.y2 - w.y1;
  const len = Math.hypot(dx, dy) || 1;
  return { dx, dy, len, ux: dx / len, uy: dy / len, nx: -dy / len, ny: dx / len };
};
const wallPoint = (w, tt) => ({ x: w.x1 + (w.x2 - w.x1) * tt, y: w.y1 + (w.y2 - w.y1) * tt });
const projectToWall = (cm, w) => {
  const v = wallVec(w);
  const px = cm.x - w.x1, py = cm.y - w.y1;
  const dot = px * v.ux + py * v.uy;
  const tt = Math.max(0, Math.min(1, dot / v.len));
  const proj = wallPoint(w, tt);
  return { t: tt, dist: Math.hypot(cm.x - proj.x, cm.y - proj.y), x: proj.x, y: proj.y };
};

function GuidedCanvas({ onSubmit, projectName }) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const [tool, setTool] = useState("wall");
  // NOTE: walls/doors/windows/pending/cursor coordinates are stored in CM (not pixels)
  const [walls, setWalls] = useState([]);       // {x1,y1,x2,y2} cm
  const [doors, setDoors] = useState([]);       // {wallIndex, t: 0..1 along wall, width: cm}
  const [windows, setWindows] = useState([]);   // {wallIndex, t: 0..1 along wall, width: cm}
  const [pending, setPending] = useState(null); // {x,y} cm
  const [cursorCm, setCursorCm] = useState(null); // {x,y} cm (updated on mousemove)
  const [lengthInput, setLengthInput] = useState(""); // typed length while pending
  const [step, setStep] = useState(0);
  const [restored, setRestored] = useState(false);
  const [texts, setTexts] = useState([]);       // {id,x,y (cm),content}
  const [erasedStack, setErasedStack] = useState([]); // (deprecated) legacy
  const [history, setHistory] = useState([]);          // unified undo stack {kind, ...}
  const [panPx, setPanPx] = useState({ x: 0, y: 0 });
  const panRef = useRef({ x: 0, y: 0, startX: 0, startY: 0, dragging: false });
  const cmPxRef = useRef(DEFAULT_CM_PX);
  const dragRef = useRef(null);
  const inputRef = useRef(null);
  const [zoomOverride, setZoomOverride] = useState(null); // null=auto, else px/cm
  const [hovered, setHovered] = useState(null);          // {type,index} for eraser
  const [selectedOpening, setSelectedOpening] = useState(null); // {kind:"door"|"window", index}
  const [editingWallLen, setEditingWallLen] = useState(null); // wall index being length-edited
  const openingDragRef = useRef({ active: false });
  const justDraggedRef = useRef(false);
  const [interacting, setInteracting] = useState(false);
  const interactTimer = useRef(null);
  const markInteracting = () => {
    setInteracting(true);
    if (interactTimer.current) { clearTimeout(interactTimer.current); interactTimer.current = null; }
  };
  const clearInteracting = () => {
    if (interactTimer.current) clearTimeout(interactTimer.current);
    interactTimer.current = setTimeout(() => setInteracting(false), 900);
  };
  // Mobile guide: collapses to icon while drawing, auto-reveals after 10s idle
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);
  useEffect(() => {
    if (interacting) { setMobileGuideOpen(false); return; }
    const to = setTimeout(() => setMobileGuideOpen(true), 10000);
    return () => clearTimeout(to);
  }, [interacting]);

  // Compute px-per-cm to fit current bbox (or use manual override).
  // Uses only wall endpoints (openings live along walls, so walls bbox suffices).
  // Adds a 2m (200cm) margin on each side so the drawing has breathing room.
  const FIT_MARGIN_CM = 200;
  const computeCmPx = () => {
    if (zoomOverride) return clampCmPx(zoomOverride);
    const c = canvasRef.current; if (!c) return DEFAULT_CM_PX;
    if (walls.length === 0) return DEFAULT_CM_PX;
    const xs = walls.flatMap((w) => [w.x1, w.x2]);
    const ys = walls.flatMap((w) => [w.y1, w.y2]);
    const w_cm = (Math.max(...xs) - Math.min(...xs)) + FIT_MARGIN_CM * 2;
    const h_cm = (Math.max(...ys) - Math.min(...ys)) + FIT_MARGIN_CM * 2;
    if (w_cm === 0 && h_cm === 0) return DEFAULT_CM_PX;
    const availW = c.offsetWidth - CANVAS_PADDING * 2;
    const availH = c.offsetHeight - CANVAS_PADDING * 2;
    const s = Math.min(w_cm > 0 ? availW / w_cm : Infinity, h_cm > 0 ? availH / h_cm : Infinity);
    return clampCmPx(Math.min(DEFAULT_CM_PX, s));
  };
  // Fit-to-content: sets zoom + panPx so the drawing (+ 2m margin) is fully visible AND centered.
  const zoomToFit = () => {
    const c = canvasRef.current; if (!c) return;
    if (walls.length === 0) { setZoomOverride(null); setPanPx({ x: 0, y: 0 }); return; }
    const xs = walls.flatMap((w) => [w.x1, w.x2]);
    const ys = walls.flatMap((w) => [w.y1, w.y2]);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const w_cm = (maxX - minX) + FIT_MARGIN_CM * 2;
    const h_cm = (maxY - minY) + FIT_MARGIN_CM * 2;
    const availW = c.offsetWidth, availH = c.offsetHeight;
    const s = Math.min(availW / w_cm, availH / h_cm);
    const cmPx = clampCmPx(Math.min(DEFAULT_CM_PX, s));
    // Center: canvas center = bbox center * cmPx + panPx  →  panPx = center - bboxCenter*cmPx
    const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
    setZoomOverride(cmPx);
    setPanPx({ x: availW / 2 - cx * cmPx, y: availH / 2 - cy * cmPx });
  };

  useEffect(() => {
    const d = loadDraft();
    if (d && d.mode === "guided") {
      const wallsHere = Array.isArray(d.walls) ? d.walls : [];
      if (wallsHere.length) setWalls(wallsHere);
      // Migrate legacy openings ({x,y}) → new anchored format ({wallIndex,t,width})
      const migrate = (arr, defW) => (arr || []).map((o) => {
        if (o && typeof o.wallIndex === "number") return o;
        if (!wallsHere.length) return null;
        let best = null;
        for (let i = 0; i < wallsHere.length; i++) {
          const proj = projectToWall({ x: o.x ?? 0, y: o.y ?? 0 }, wallsHere[i]);
          if (!best || proj.dist < best.dist) best = { wallIndex: i, t: proj.t, width: defW, dist: proj.dist };
        }
        return best ? { wallIndex: best.wallIndex, t: best.t, width: best.width } : null;
      }).filter(Boolean);
      if (Array.isArray(d.doors)) setDoors(migrate(d.doors, DEFAULT_DOOR_W));
      if (Array.isArray(d.windows)) setWindows(migrate(d.windows, DEFAULT_WINDOW_W));
      if (Array.isArray(d.texts)) setTexts(d.texts);
      if ((d.walls?.length || 0) + (d.doors?.length || 0) + (d.windows?.length || 0) + (d.texts?.length || 0) > 0) {
        setRestored(true);
        toast.success("Bozza ripristinata");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- restore draft once on mount; only stable setters/constants used
  }, []);

  useEffect(() => {
    if (!restored && walls.length === 0 && doors.length === 0 && windows.length === 0 && texts.length === 0) return;
    saveDraft({ mode: "guided", projectName, walls, doors, windows, texts });
  }, [walls, doors, windows, texts, projectName, restored]);

  const draw = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = c.offsetWidth; c.height = c.offsetHeight;
    const ctx = c.getContext("2d");
    const CM_PX = computeCmPx();
    cmPxRef.current = CM_PX;
    // Background
    ctx.fillStyle = "#FBFAF7"; ctx.fillRect(0, 0, c.width, c.height);
    // Apply pan for all drawing after grid (grid stays anchored to viewport for readability)
    ctx.save();
    ctx.translate(panPx.x, panPx.y);
    // Millimeter-paper grid — INFINITE: extends beyond viewport based on pan
    // ctx is translated by panPx; grid in world coords must span the visible viewport
    const worldMinX = -panPx.x, worldMaxX = c.width - panPx.x;
    const worldMinY = -panPx.y, worldMaxY = c.height - panPx.y;
    const drawGrid = (step, style) => {
      ctx.strokeStyle = style;
      const sx = Math.floor(worldMinX / step) * step;
      const ex = Math.ceil(worldMaxX / step) * step;
      const sy = Math.floor(worldMinY / step) * step;
      const ey = Math.ceil(worldMaxY / step) * step;
      for (let x = sx; x <= ex; x += step) { ctx.beginPath(); ctx.moveTo(x, sy); ctx.lineTo(x, ey); ctx.stroke(); }
      for (let y = sy; y <= ey; y += step) { ctx.beginPath(); ctx.moveTo(sx, y); ctx.lineTo(ex, y); ctx.stroke(); }
    };
    ctx.lineWidth = 1;
    drawGrid(CM_PX, "#EFEDE8");
    drawGrid(CM_PX * 5, "#DDDAD3");
    drawGrid(CM_PX * 10, "#C7C3BC");
    // Walls WITH BREAKS for anchored openings (doors + windows)
    walls.forEach((w, i) => {
      const highlighted = hovered?.type === "wall" && hovered.index === i;
      const v = wallVec(w);
      // Collect openings on this wall as normalized t-ranges
      const ranges = [];
      doors.forEach((d) => { if (d.wallIndex === i) { const h = (d.width / 2) / v.len; ranges.push({ a: Math.max(0, d.t - h), b: Math.min(1, d.t + h) }); } });
      windows.forEach((wn) => { if (wn.wallIndex === i) { const h = (wn.width / 2) / v.len; ranges.push({ a: Math.max(0, wn.t - h), b: Math.min(1, wn.t + h) }); } });
      ranges.sort((r1, r2) => r1.a - r2.a);
      // Merge overlapping gaps
      const merged = [];
      ranges.forEach((r) => {
        if (merged.length && r.a <= merged[merged.length - 1].b) merged[merged.length - 1].b = Math.max(merged[merged.length - 1].b, r.b);
        else merged.push({ ...r });
      });
      // Draw wall segments between gaps
      ctx.strokeStyle = highlighted ? "#DC2626" : "#1C1917"; ctx.lineWidth = 4; ctx.setLineDash([]);
      const seg = (ta, tb) => {
        if (tb - ta < 1e-6) return;
        const p1 = wallPoint(w, ta), p2 = wallPoint(w, tb);
        ctx.beginPath();
        ctx.moveTo(p1.x * CM_PX, p1.y * CM_PX);
        ctx.lineTo(p2.x * CM_PX, p2.y * CM_PX);
        ctx.stroke();
      };
      let cur = 0;
      merged.forEach((g) => { if (g.a > cur) seg(cur, g.a); cur = Math.max(cur, g.b); });
      if (cur < 1) seg(cur, 1);
      // Wall length label handled by clickable HTML overlay outside canvas (always in cm)
    });
    // Doors anchored to walls with directional swing arc
    doors.forEach((d, i) => {
      const w = walls[d.wallIndex]; if (!w) return;
      const v = wallVec(w);
      const half = d.width / 2;
      const c = wallPoint(w, d.t);
      const p1 = { x: c.x - v.ux * half, y: c.y - v.uy * half };
      const p2 = { x: c.x + v.ux * half, y: c.y + v.uy * half };
      const isSelected = selectedOpening?.kind === "door" && selectedOpening.index === i;
      const highlighted = hovered?.type === "door" && hovered.index === i;
      const color = isSelected ? "#D06D4D" : highlighted ? "#DC2626" : "#059669";
      ctx.strokeStyle = color; ctx.setLineDash([]);
      // Jambs (short ticks perpendicular to the wall at both ends of the opening)
      const tickLen = 8; // cm outward
      const j1 = { x: p1.x + v.nx * tickLen, y: p1.y + v.ny * tickLen };
      const j2 = { x: p2.x + v.nx * tickLen, y: p2.y + v.ny * tickLen };
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(p1.x * CM_PX, p1.y * CM_PX); ctx.lineTo(j1.x * CM_PX, j1.y * CM_PX);
      ctx.moveTo(p2.x * CM_PX, p2.y * CM_PX); ctx.lineTo(j2.x * CM_PX, j2.y * CM_PX);
      ctx.stroke();
      // Swing arc — hinge at p1, radius = width, opens toward wall normal
      ctx.lineWidth = 2; ctx.setLineDash([4, 3]);
      const a0 = Math.atan2(v.uy, v.ux);
      const a1 = a0 + Math.PI / 2;
      ctx.beginPath();
      ctx.arc(p1.x * CM_PX, p1.y * CM_PX, d.width * CM_PX, a0, a1);
      ctx.stroke();
      // Open leaf line from hinge to tip
      const leaf = { x: p1.x + v.nx * d.width, y: p1.y + v.ny * d.width };
      ctx.beginPath();
      ctx.moveTo(p1.x * CM_PX, p1.y * CM_PX); ctx.lineTo(leaf.x * CM_PX, leaf.y * CM_PX);
      ctx.stroke();
      ctx.setLineDash([]);
      // Width label
      ctx.fillStyle = color; ctx.font = "10px Manrope";
      ctx.fillText(`${d.width} cm`, c.x * CM_PX + 4, c.y * CM_PX - 4);
    });
    // Windows anchored to walls (double-line along wall)
    windows.forEach((wn, i) => {
      const w = walls[wn.wallIndex]; if (!w) return;
      const v = wallVec(w);
      const half = wn.width / 2;
      const c = wallPoint(w, wn.t);
      const p1 = { x: c.x - v.ux * half, y: c.y - v.uy * half };
      const p2 = { x: c.x + v.ux * half, y: c.y + v.uy * half };
      const isSelected = selectedOpening?.kind === "window" && selectedOpening.index === i;
      const highlighted = hovered?.type === "window" && hovered.index === i;
      const color = isSelected ? "#D06D4D" : highlighted ? "#DC2626" : "#2563eb";
      ctx.strokeStyle = color; ctx.setLineDash([]);
      // Sill line along wall
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(p1.x * CM_PX, p1.y * CM_PX); ctx.lineTo(p2.x * CM_PX, p2.y * CM_PX);
      ctx.stroke();
      // Inner parallel line offset along normal
      const off = 4;
      const q1 = { x: p1.x + v.nx * off, y: p1.y + v.ny * off };
      const q2 = { x: p2.x + v.nx * off, y: p2.y + v.ny * off };
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(q1.x * CM_PX, q1.y * CM_PX); ctx.lineTo(q2.x * CM_PX, q2.y * CM_PX);
      ctx.stroke();
      // Jamb ticks connecting inner/outer
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(p1.x * CM_PX, p1.y * CM_PX); ctx.lineTo(q1.x * CM_PX, q1.y * CM_PX);
      ctx.moveTo(p2.x * CM_PX, p2.y * CM_PX); ctx.lineTo(q2.x * CM_PX, q2.y * CM_PX);
      ctx.stroke();
      // Width label
      ctx.fillStyle = color; ctx.font = "10px Manrope";
      ctx.fillText(`${wn.width} cm`, c.x * CM_PX + 4, c.y * CM_PX - 4);
    });
    // Pending point + rubber-band line to cursor (with angle/close snap)
    if (pending) {
      const px = pending.x * CM_PX, py = pending.y * CM_PX;
      const snapped = applySnap(pending, cursorCm);
      const stroke = snapped?.snapType === "axis" ? "#059669"
                    : snapped?.snapType === "diag" ? "#2563eb"
                    : snapped?.snapType === "origin" ? "#059669"
                    : snapped?.snapType === "endpoint" ? "#0891b2"
                    : "#D06D4D";
      ctx.strokeStyle = stroke; ctx.lineWidth = 2; ctx.setLineDash([5, 4]);
      ctx.beginPath(); ctx.arc(px, py, 6, 0, Math.PI * 2); ctx.stroke();
      if (snapped) {
        const sx = snapped.x * CM_PX, sy = snapped.y * CM_PX;
        ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(sx, sy); ctx.stroke();
        ctx.setLineDash([]);
        // Live distance label near cursor
        const dist_cm = Math.round(Math.hypot(snapped.x - pending.x, snapped.y - pending.y));
        if (dist_cm > 0) {
          const mid_x = (px + sx) / 2, mid_y = (py + sy) / 2;
          const label = `${dist_cm} cm`;
          ctx.font = "bold 13px Manrope, sans-serif";
          const tw = ctx.measureText(label).width;
          ctx.fillStyle = "rgba(255,255,255,0.92)";
          ctx.fillRect(mid_x + 8, mid_y - 20, tw + 12, 20);
          ctx.strokeStyle = stroke; ctx.lineWidth = 1;
          ctx.strokeRect(mid_x + 8, mid_y - 20, tw + 12, 20);
          ctx.fillStyle = stroke;
          ctx.fillText(label, mid_x + 14, mid_y - 5);
        }
        // Emphasize snap target
        if (snapped.snapType === "origin") {
          ctx.fillStyle = "#059669"; ctx.beginPath(); ctx.arc(sx, sy, 8, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = "#059669"; ctx.font = "11px Manrope"; ctx.fillText("Chiudi", sx + 12, sy - 12);
        } else if (snapped.snapType === "endpoint") {
          ctx.fillStyle = "#0891b2"; ctx.beginPath(); ctx.arc(sx, sy, 7, 0, Math.PI * 2); ctx.fill();
          ctx.strokeStyle = "#ffffff"; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(sx, sy, 7, 0, Math.PI * 2); ctx.stroke();
          ctx.fillStyle = "#0891b2"; ctx.font = "11px Manrope"; ctx.fillText("Aggancia", sx + 12, sy - 12);
        } else if (snapped.snapType) {
          ctx.fillStyle = stroke; ctx.beginPath(); ctx.arc(sx, sy, 4, 0, Math.PI * 2); ctx.fill();
        }
      }
      ctx.setLineDash([]);
    }
    ctx.restore();
  };

  useEffect(() => { draw(); }, [walls, doors, windows, pending, cursorCm, zoomOverride, hovered, panPx]);
  useEffect(() => {
    const onResize = () => draw();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line
  }, []);

  // Auto-focus length input when pending appears
  useEffect(() => { if (pending && inputRef.current) inputRef.current.focus(); }, [pending]);

  // FIX: separate X/Y so panPx offset is applied on the right axis after zoom-at-cursor
  const pxToCmX = (px) => (px - panPx.x) / cmPxRef.current;
  const pxToCmY = (py) => (py - panPx.y) / cmPxRef.current;
  const snapCm = (v) => Math.round(v); // snap to nearest cm

  // Zoom keeping the canvas center anchored (used by +/- buttons)
  const zoomAtCenter = (factor) => {
    const c = canvasRef.current; if (!c) return;
    const cx = c.offsetWidth / 2, cy = c.offsetHeight / 2;
    const cur = cmPxRef.current;
    const nz = clampCmPx(cur * factor);
    if (nz === cur) return;
    const k = nz / cur;
    setPanPx((p) => ({ x: cx - k * (cx - p.x), y: cy - k * (cy - p.y) }));
    setZoomOverride(nz);
  };

  // Snap cursor to: (a) any existing wall endpoint (highest priority, 1cm exact), (b) 0/45/90° angles from pending
  const CLOSE_TOL_CM = 20;
  const ANGLE_TOL_DEG = 4;
  // Nearest endpoint of any wall within tolerance (returns { x, y, isOrigin } or null)
  const nearestEndpoint = (cm) => {
    if (!cm) return null;
    let best = null;
    for (let i = 0; i < walls.length; i++) {
      const w = walls[i];
      for (const ep of [{ x: w.x1, y: w.y1, wi: i, first: true }, { x: w.x2, y: w.y2, wi: i, first: false }]) {
        const d = Math.hypot(cm.x - ep.x, cm.y - ep.y);
        if (d <= CLOSE_TOL_CM && (!best || d < best.dist)) best = { x: Math.round(ep.x), y: Math.round(ep.y), dist: d, isOrigin: (i === 0 && ep.first) };
      }
    }
    return best;
  };
  const applySnap = (from, cur) => {
    if (!from || !cur) return { ...cur, snapType: null };
    // (a) endpoint snap — snaps to any existing wall endpoint (1cm precision via Math.round)
    const ep = nearestEndpoint(cur);
    if (ep) return { x: ep.x, y: ep.y, snapType: ep.isOrigin ? "origin" : "endpoint" };
    // (b) angle snap
    const dx = cur.x - from.x, dy = cur.y - from.y;
    const dist = Math.hypot(dx, dy);
    if (dist < 1) return { ...cur, snapType: null };
    const ang = Math.atan2(dy, dx) * 180 / Math.PI;
    const targets = [-180, -135, -90, -45, 0, 45, 90, 135, 180];
    for (const t of targets) {
      if (Math.abs(ang - t) < ANGLE_TOL_DEG) {
        const rad = t * Math.PI / 180;
        return {
          x: Math.round(from.x + Math.cos(rad) * dist),
          y: Math.round(from.y + Math.sin(rad) * dist),
          snapType: (t % 90 === 0) ? "axis" : "diag",
        };
      }
    }
    return { ...cur, snapType: null };
  };

  // Hit test for eraser
  const distPointToSegment = (px, py, x1, y1, x2, y2) => {
    const A = px - x1, B = py - y1, C = x2 - x1, D = y2 - y1;
    const len_sq = C * C + D * D;
    const t = len_sq ? Math.max(0, Math.min(1, (A * C + B * D) / len_sq)) : 0;
    const xx = x1 + t * C, yy = y1 + t * D;
    return Math.hypot(px - xx, py - yy);
  };
  // Nearest wall — used to snap doors/windows on placement
  const nearestWall = (cm) => {
    let best = null;
    for (let i = 0; i < walls.length; i++) {
      const p = projectToWall(cm, walls[i]);
      if (!best || p.dist < best.dist) best = { ...p, wallIndex: i };
    }
    return best;
  };
  // Hit test for existing opening (used by drag + eraser)
  const findOpeningAt = (cm) => {
    for (let i = 0; i < doors.length; i++) {
      const w = walls[doors[i].wallIndex]; if (!w) continue;
      const c = wallPoint(w, doors[i].t);
      if (Math.hypot(cm.x - c.x, cm.y - c.y) <= Math.max(15, doors[i].width / 2)) return { type: "door", index: i };
    }
    for (let i = 0; i < windows.length; i++) {
      const w = walls[windows[i].wallIndex]; if (!w) continue;
      const c = wallPoint(w, windows[i].t);
      if (Math.hypot(cm.x - c.x, cm.y - c.y) <= Math.max(15, windows[i].width / 2)) return { type: "window", index: i };
    }
    return null;
  };
  const findHitAt = (cm) => {
    if (!cm) return null;
    const op = findOpeningAt(cm); if (op) return op;
    for (let i = 0; i < walls.length; i++) {
      const w = walls[i];
      if (distPointToSegment(cm.x, cm.y, w.x1, w.y1, w.x2, w.y2) <= 8) return { type: "wall", index: i };
    }
    return null;
  };
  // Drag an opening along its anchor wall (mouse + touch via pointer events on window)
  const startOpeningDrag = (kind, index) => {
    openingDragRef.current = { active: true, kind, index, moved: false };
    const onMove = (ev) => {
      if (!openingDragRef.current.active) return;
      const c = canvasRef.current; if (!c) return;
      const rect = c.getBoundingClientRect();
      const cm = { x: pxToCmX(ev.clientX - rect.left), y: pxToCmY(ev.clientY - rect.top) };
      openingDragRef.current.moved = true;
      if (kind === "door") {
        setDoors((prev) => prev.map((d, i) => {
          if (i !== index) return d;
          const w = walls[d.wallIndex]; if (!w) return d;
          const proj = projectToWall(cm, w);
          const halfN = (d.width / 2) / wallVec(w).len;
          const tt = Math.max(halfN, Math.min(1 - halfN, proj.t));
          return { ...d, t: tt };
        }));
      } else {
        setWindows((prev) => prev.map((wn, i) => {
          if (i !== index) return wn;
          const w = walls[wn.wallIndex]; if (!w) return wn;
          const proj = projectToWall(cm, w);
          const halfN = (wn.width / 2) / wallVec(w).len;
          const tt = Math.max(halfN, Math.min(1 - halfN, proj.t));
          return { ...wn, t: tt };
        }));
      }
    };
    const onUp = () => {
      const wasDragged = openingDragRef.current.moved;
      openingDragRef.current = { active: false };
      justDraggedRef.current = wasDragged;
      setTimeout(() => { justDraggedRef.current = false; }, 200);
      if (!wasDragged) setSelectedOpening({ kind, index });
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const onCanvasMove = (e) => {
    const c = canvasRef.current; if (!c) return;
    const rect = c.getBoundingClientRect();
    const cm = { x: snapCm(pxToCmX(e.clientX - rect.left)), y: snapCm(pxToCmY(e.clientY - rect.top)) };
    setCursorCm(cm);
    if (tool === "eraser") setHovered(findHitAt(cm));
    else if (hovered) setHovered(null);
  };

  const handleClick = (e) => {
    const c = canvasRef.current; const rect = c.getBoundingClientRect();
    const rawX = e.clientX - rect.left, rawY = e.clientY - rect.top;
    if (tool === "text") {
      const id = Date.now() + Math.random();
      setTexts([...texts, { id, x: pxToCmX(rawX), y: pxToCmY(rawY), content: "Testo" }]);
      return;
    }
    if (tool === "eraser") {
      const cm = { x: pxToCmX(rawX), y: pxToCmY(rawY) };
      const hit = findHitAt(cm);
      if (hit) {
        if (hit.type === "wall") {
          setHistory((h) => [...h, { kind: "erase", type: "wall", item: walls[hit.index], index: hit.index }]);
          setWalls(walls.filter((_, i) => i !== hit.index));
        } else if (hit.type === "door") {
          setHistory((h) => [...h, { kind: "erase", type: "door", item: doors[hit.index], index: hit.index }]);
          setDoors(doors.filter((_, i) => i !== hit.index));
        } else if (hit.type === "window") {
          setHistory((h) => [...h, { kind: "erase", type: "window", item: windows[hit.index], index: hit.index }]);
          setWindows(windows.filter((_, i) => i !== hit.index));
        }
        setHovered(null);
      }
      return;
    }
    if (tool === "pan") return;
    if (justDraggedRef.current) return; // suppress click after drag
    const p = { x: snapCm(pxToCmX(rawX)), y: snapCm(pxToCmY(rawY)) };
    if (tool === "wall") {
      if (!pending) {
        // Snap first point to nearest existing endpoint (1 cm precision)
        const ep = nearestEndpoint({ x: pxToCmX(rawX), y: pxToCmY(rawY) });
        setPending(ep ? { x: ep.x, y: ep.y } : p);
        setLengthInput("");
      }
      else {
        // Coarse pointer (touch/mobile): 2nd tap just sets direction → open length input, don't commit
        if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer: coarse)").matches) {
          setCursorCm(p);
          setTimeout(() => inputRef.current?.focus(), 60);
          return;
        }
        const snapped = applySnap(pending, p);
        const newWall = { x1: pending.x, y1: pending.y, x2: snapped.x, y2: snapped.y };
        setWalls([...walls, newWall]);
        setHistory((h) => [...h, { kind: "add", type: "wall", item: newWall }]);
        setPending(null); setLengthInput("");
      }
    } else if (tool === "door" || tool === "window") {
      if (walls.length === 0) { toast.error("Disegna prima almeno una parete"); return; }
      const cm = { x: pxToCmX(rawX), y: pxToCmY(rawY) };
      const nw = nearestWall(cm);
      if (!nw || nw.dist > 40) { toast.error("Clicca vicino a una parete"); return; }
      const defW = tool === "door" ? DEFAULT_DOOR_W : DEFAULT_WINDOW_W;
      const vlen = wallVec(walls[nw.wallIndex]).len;
      const halfN = (defW / 2) / vlen;
      const tt = Math.max(halfN, Math.min(1 - halfN, nw.t));
      const item = { wallIndex: nw.wallIndex, t: tt, width: defW };
      if (tool === "door") {
        setDoors([...doors, item]);
        setHistory((h) => [...h, { kind: "add", type: "door", item }]);
        setSelectedOpening({ kind: "door", index: doors.length });
      } else {
        setWindows([...windows, item]);
        setHistory((h) => [...h, { kind: "add", type: "window", item }]);
        setSelectedOpening({ kind: "window", index: windows.length });
      }
    }
  };

  const commitTypedLength = () => {
    const raw = (lengthInput || "").replace(",", ".").trim();
    const len_cm = parseFloat(raw);
    if (!pending || !cursorCm || !isFinite(len_cm) || len_cm <= 0) return;
    const dx = cursorCm.x - pending.x, dy = cursorCm.y - pending.y;
    const dist = Math.hypot(dx, dy) || 1;
    const ux = dx / dist, uy = dy / dist;
    const end = { x: Math.round(pending.x + ux * len_cm), y: Math.round(pending.y + uy * len_cm) };
    const newWall = { x1: pending.x, y1: pending.y, x2: end.x, y2: end.y };
    setWalls([...walls, newWall]);
    setHistory((h) => [...h, { kind: "add", type: "wall", item: newWall }]);
    setPending(null); setLengthInput("");
  };

  const clearAll = () => { setWalls([]); setDoors([]); setWindows([]); setPending(null); setTexts([]); setLengthInput(""); setErasedStack([]); setHistory([]); setSelectedOpening(null); saveDraft({ mode: "guided", projectName, walls: [], doors: [], windows: [], texts: [] }); };
  const undo = () => {
    if (history.length === 0) return;
    const last = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    if (last.kind === "add") {
      if (last.type === "wall") setWalls((prev) => prev.slice(0, -1));
      else if (last.type === "door") setDoors((prev) => prev.slice(0, -1));
      else if (last.type === "window") setWindows((prev) => prev.slice(0, -1));
    } else if (last.kind === "erase") {
      const insert = (arr) => { const c = [...arr]; c.splice(Math.min(last.index, c.length), 0, last.item); return c; };
      if (last.type === "wall") setWalls((prev) => insert(prev));
      else if (last.type === "door") setDoors((prev) => insert(prev));
      else if (last.type === "window") setWindows((prev) => insert(prev));
    }
  };

  // Keyboard shortcut: Ctrl/Cmd+Z for global undo
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "z" || e.key === "Z")) {
        const tag = (e.target && e.target.tagName) || "";
        if (tag === "INPUT" || tag === "TEXTAREA") return;
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [history]);

  // Text drag/edit handlers — pointer events for mouse+touch; positions in cm
  const startDrag = (e, id) => {
    e.stopPropagation();
    const rect = wrapperRef.current.getBoundingClientRect();
    const tx = texts.find((t) => t.id === id);
    const CM = cmPxRef.current;
    dragRef.current = { id, offsetX: e.clientX - rect.left - (tx.x * CM + panPx.x), offsetY: e.clientY - rect.top - (tx.y * CM + panPx.y) };
    const onMove = (ev) => {
      if (!dragRef.current) return;
      ev.preventDefault();
      const r = wrapperRef.current.getBoundingClientRect();
      const nx = (ev.clientX - r.left - dragRef.current.offsetX - panPx.x) / cmPxRef.current;
      const ny = (ev.clientY - r.top - dragRef.current.offsetY - panPx.y) / cmPxRef.current;
      setTexts((prev) => prev.map((t) => t.id === dragRef.current.id ? { ...t, x: nx, y: ny } : t));
    };
    const onUp = () => { dragRef.current = null; window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };
  const updateText = (id, content) => setTexts((prev) => prev.map((t) => t.id === id ? { ...t, content } : t));
  const deleteText = (id) => setTexts((prev) => prev.filter((t) => t.id !== id));

  const done = () => {
    const c = canvasRef.current; const ctx = c.getContext("2d");
    const CM = cmPxRef.current;
    texts.forEach((tx) => {
      if (!tx.content) return;
      ctx.fillStyle = "#1C1917";
      ctx.font = "14px Manrope, system-ui, sans-serif";
      ctx.textBaseline = "top";
      ctx.fillText(tx.content, tx.x * CM, tx.y * CM);
    });
    // Geometry in meters for DXF (y flipped). Openings resolved from wall anchor.
    const openingToWorld = (o) => {
      const w = walls[o.wallIndex]; if (!w) return null;
      const c = wallPoint(w, o.t);
      const v = wallVec(w);
      return {
        x: c.x / 100, y: -c.y / 100,
        width: o.width / 100,
        angle: Math.atan2(-v.uy, v.ux), // radians; DXF y-up
      };
    };
    const geom = {
      walls: walls.map((w) => ({ x1: w.x1 / 100, y1: -w.y1 / 100, x2: w.x2 / 100, y2: -w.y2 / 100 })),
      doors: doors.map(openingToWorld).filter(Boolean),
      windows: windows.map(openingToWorld).filter(Boolean),
    };
    const png = canvasRef.current.toDataURL("image/png");
    draw();
    onSubmit({ mode: "guided", png_data_url: png, geometry: geom });
  };

  const guides = [t("draw.guidedGuide1"), t("draw.guidedGuide2"), t("draw.guidedGuide3")];

  return (
    <div className="min-h-screen bg-white relative">
      <div className="absolute top-4 left-4 right-4 z-20 flex items-center justify-between gap-3">
        <button data-testid="draw-back" onClick={() => window.history.back()} className="canvas-glass px-4 py-2 rounded-full text-sm inline-flex items-center gap-2 text-stone-900">
          <ArrowLeft size={16} /> {t("draw.back")}
        </button>
        {projectName && (
          <div data-testid="draw-project-badge" className="canvas-glass px-4 py-2 rounded-full text-xs uppercase tracking-widest text-stone-700 truncate max-w-[40vw]">
            {projectName}
          </div>
        )}
        <button data-testid="draw-guided-submit" onClick={done} className="bg-[#D06D4D] text-white px-6 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 hover:bg-[#b95a3d]">
          <Send size={14} /> {t("draw.form.send")}
        </button>
      </div>

      {/* Descriptive info above canvas — DESKTOP ONLY */}
      <div className="hidden lg:block absolute top-20 left-10 right-10 z-10 bg-white/85 backdrop-blur-sm border border-stone-200 rounded-xl px-6 py-4 shadow-[0_2px_8px_rgba(28,25,23,0.04)]">
        <div className="text-sm text-stone-700 leading-relaxed space-y-2">
          <p>
            <span className="font-semibold text-stone-900">Precisione al centimetro</span> con misure esatte e aggancio automatico.
          </p>
          <ul>
            <li className="flex gap-2">
              <span className="text-[#D06D4D] mt-1">•</span>
              <span><span className="font-semibold text-stone-900">Come funziona:</span> Clicca il primo punto, poi digita la lunghezza in cm e premi Invio per creare la parete nella direzione del cursore. Zoom automatico in base alle dimensioni della stanza. Puoi aggiungere note usando il tasto T.</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Guide bubble — DESKTOP */}
      <div className="hidden lg:block absolute top-52 left-4 z-20 canvas-glass p-4 rounded-2xl max-w-xs pointer-events-none">
        <div className="eyebrow">Guida {step + 1}/3</div>
        <p className="mt-2 text-stone-800 text-sm">{guides[step]}</p>
        <div className="mt-3 flex gap-2 pointer-events-auto">
          {step > 0 && (
            <button data-testid="draw-guide-prev" onClick={() => setStep((s) => s - 1)} className="text-xs bg-stone-200 text-stone-900 px-3 py-1 rounded-full hover:bg-stone-300 transition-colors">←</button>
          )}
          <button data-testid="draw-guide-next" onClick={() => setStep((s) => (s + 1) % 3)} className="text-xs bg-stone-900 text-white px-3 py-1 rounded-full">→</button>
        </div>
      </div>

      {/* MOBILE: collapsible guide (icon → expanded card, auto-open after 10s idle) */}
      <div className="lg:hidden absolute top-16 left-3 z-30">
        {mobileGuideOpen ? (
          <div data-testid="mobile-guide-panel" className="canvas-glass p-3 rounded-2xl max-w-[260px] shadow-lg">
            <div className="flex items-start justify-between gap-2">
              <div className="eyebrow text-[10px]">Guida {step + 1}/3</div>
              <button data-testid="mobile-guide-close" onClick={() => setMobileGuideOpen(false)} className="text-stone-500 hover:text-stone-900 -mt-1"><X size={14} /></button>
            </div>
            <p className="mt-1 text-stone-800 text-xs leading-snug">{guides[step]}</p>
            <div className="mt-2 flex gap-1.5">
              {step > 0 && (
                <button data-testid="draw-guide-prev" onClick={() => setStep((s) => s - 1)} className="text-[11px] bg-stone-200 text-stone-900 px-2.5 py-0.5 rounded-full">←</button>
              )}
              <button data-testid="draw-guide-next" onClick={() => setStep((s) => (s + 1) % 3)} className="text-[11px] bg-stone-900 text-white px-2.5 py-0.5 rounded-full">→</button>
            </div>
          </div>
        ) : (
          <button data-testid="mobile-guide-icon" onClick={() => setMobileGuideOpen(true)} className="canvas-glass p-2 rounded-full text-stone-800 shadow hover:bg-stone-100 flex items-center gap-1.5 px-3">
            <HelpCircle size={16} /><span className="text-[11px] uppercase tracking-widest">Guida</span>
          </button>
        )}
      </div>

      <div className="absolute bottom-3 md:bottom-6 left-1/2 -translate-x-1/2 z-20 canvas-glass px-2 py-2 md:px-3 md:py-3 rounded-full flex items-center gap-1 md:gap-2 max-w-[calc(100vw-16px)] overflow-x-auto no-scrollbar">
        {[
          { k: "wall", i: <Menu size={18} /> },
          { k: "door", i: <DoorOpen size={18} /> },
          { k: "window", i: <Square size={18} /> },
          { k: "text", i: <Type size={18} /> },
          { k: "eraser", i: <Eraser size={18} /> },
          { k: "pan", i: <Hand size={18} /> },
        ].map((b) => (
          <button
            key={b.k}
            data-testid={`guided-tool-${b.k}`}
            onClick={() => { setTool(b.k); setPending(null); setLengthInput(""); setHovered(null); setSelectedOpening(null); }}
            title={t(`draw.guidedTools.${b.k}`)}
            className={`shrink-0 p-1.5 md:p-2 rounded-full transition-colors ${tool === b.k ? "bg-stone-900 text-white" : "hover:bg-stone-200 text-stone-800"}`}
          >{b.i}</button>
        ))}
        <div className="w-px h-6 bg-stone-300 mx-0.5 md:mx-1 shrink-0" />
        <button data-testid="guided-undo" onClick={undo} className="shrink-0 p-1.5 md:p-2 rounded-full hover:bg-stone-200 text-stone-800"><Undo2 size={18} /></button>
        <button data-testid="guided-clear" onClick={clearAll} className="shrink-0 p-1.5 md:p-2 rounded-full hover:bg-stone-200 text-stone-800"><Trash2 size={18} /></button>
      </div>

      <div ref={wrapperRef} className="absolute inset-x-0 top-14 bottom-20 lg:inset-x-10 lg:top-48 lg:bottom-28 rounded-none lg:rounded-md border-0 lg:border lg:border-stone-300 overflow-hidden shadow-[inset_0_0_40px_rgba(28,25,23,0.10)] bg-[#FBFAF7]">
        {/* Zoom cluster (top-right of canvas) — moved out of tool dock to fit mobile */}
        <div className="absolute top-3 right-3 z-10 canvas-glass px-2 py-1 rounded-full flex items-center gap-1">
          <button data-testid="guided-zoom-out" onClick={() => zoomAtCenter(1/1.2)} title="Zoom -" className="p-1.5 rounded-full hover:bg-stone-200 text-stone-800"><ZoomOut size={16} /></button>
          <button data-testid="guided-zoom-in" onClick={() => zoomAtCenter(1.2)} title="Zoom +" className="p-1.5 rounded-full hover:bg-stone-200 text-stone-800"><ZoomIn size={16} /></button>
          <button data-testid="guided-zoom-fit" onClick={zoomToFit} title="Adatta" className={`p-1.5 rounded-full transition-colors hover:bg-stone-200 text-stone-800`}><Maximize2 size={16} /></button>
        </div>
        <canvas
          ref={canvasRef}
          data-testid="guided-canvas"
          style={{ touchAction: "none" }}
          onClick={(e) => { markInteracting(); handleClick(e); clearInteracting(); }}
          onMouseMove={onCanvasMove}
          onPointerDown={(e) => {
            // Touch/pen only — mouse keeps existing tap-tap flow via onClick
            if (e.pointerType === "mouse") return;
            if (tool !== "wall") return;
            if (openingDragRef.current.active) return;
            if (!e.isPrimary) return; // second finger → pinch handled by onTouchStart
            const canvas = canvasRef.current; if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const raw = { x: pxToCmX(e.clientX - rect.left), y: pxToCmY(e.clientY - rect.top) };
            // First point: reuse the same endpoint snap used by PC handleClick
            const ep = nearestEndpoint(raw);
            const start = ep ? { x: ep.x, y: ep.y } : { x: snapCm(raw.x), y: snapCm(raw.y) };
            const pointerId = e.pointerId;
            let dragActive = false;
            let lastCm = start;
            const DRAG_THRESHOLD_CM = 3; // below this, treat as tap → let onClick take over (tap-tap)
            const onMove = (mv) => {
              if (mv.pointerId !== pointerId) return;
              mv.preventDefault();
              const r = canvas.getBoundingClientRect();
              const cm = { x: snapCm(pxToCmX(mv.clientX - r.left)), y: snapCm(pxToCmY(mv.clientY - r.top)) };
              lastCm = cm;
              if (!dragActive) {
                const distMoved = Math.hypot(cm.x - start.x, cm.y - start.y);
                if (distMoved >= DRAG_THRESHOLD_CM) {
                  dragActive = true;
                  setPending(start);
                  setLengthInput("");
                  try { canvas.setPointerCapture(pointerId); } catch (e) { console.debug("setPointerCapture unsupported", e); }
                }
              }
              if (dragActive) setCursorCm(cm);
            };
            const onUp = (up) => {
              if (up.pointerId !== pointerId) return;
              window.removeEventListener("pointermove", onMove);
              window.removeEventListener("pointerup", onUp);
              window.removeEventListener("pointercancel", onUp);
              try { canvas.releasePointerCapture(pointerId); } catch (e) { console.debug("releasePointerCapture unsupported", e); }
              if (!dragActive) return; // pure tap → let click handler run (identical to PC tap-tap)
              // Drag committed → reuse PC pipeline: applySnap for endpoint/angle snap, then push wall
              const snapped = applySnap(start, lastCm);
              const newWall = { x1: start.x, y1: start.y, x2: snapped.x, y2: snapped.y };
              setWalls((prev) => [...prev, newWall]);
              setHistory((h) => [...h, { kind: "add", type: "wall", item: newWall }]);
              setPending(null);
              setLengthInput("");
              // Suppress the synthetic click that follows touchend
              justDraggedRef.current = true;
              setTimeout(() => { justDraggedRef.current = false; }, 300);
            };
            window.addEventListener("pointermove", onMove);
            window.addEventListener("pointerup", onUp);
            window.addEventListener("pointercancel", onUp);
          }}
          onMouseDown={(e) => {
            markInteracting();
            if ((tool === "door" || tool === "window") && e.button === 0) {
              const rect = canvasRef.current.getBoundingClientRect();
              const cm = { x: pxToCmX(e.clientX - rect.left), y: pxToCmY(e.clientY - rect.top) };
              const hit = findOpeningAt(cm);
              if (hit) { e.preventDefault(); startOpeningDrag(hit.type, hit.index); return; }
            }
            if (e.button === 1 || (e.button === 0 && e.altKey) || tool === "pan") { e.preventDefault(); panRef.current = { ...panRef.current, dragging: true, startX: e.clientX, startY: e.clientY, origX: panPx.x, origY: panPx.y }; return; }
          }}
          onMouseUp={() => { panRef.current.dragging = false; clearInteracting(); }}
          onContextMenu={(e) => e.preventDefault()}
          onWheel={(e) => {
            e.preventDefault();
            const factor = e.deltaY < 0 ? 1.1 : 1/1.1;
            const rect = canvasRef.current.getBoundingClientRect();
            const cx = e.clientX - rect.left, cy = e.clientY - rect.top;
            const cur = cmPxRef.current;
            const nz = clampCmPx(cur * factor);
            const k = nz / cur;
            setPanPx((p) => ({ x: cx - k * (cx - p.x), y: cy - k * (cy - p.y) }));
            setZoomOverride(nz);
          }}
          onTouchStart={(e) => {
            markInteracting();
            if (e.touches.length === 2) {
              e.preventDefault();
              const [a, b] = e.touches;
              const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
              canvasRef.current._pinch = { d, cmpx: cmPxRef.current, cx: (a.clientX + b.clientX) / 2, cy: (a.clientY + b.clientY) / 2, px: panPx.x, py: panPx.y };
            } else if (e.touches.length === 1) {
              const tt = e.touches[0];
              if (tool === "door" || tool === "window") {
                const rect = canvasRef.current.getBoundingClientRect();
                const cm = { x: pxToCmX(tt.clientX - rect.left), y: pxToCmY(tt.clientY - rect.top) };
                const hit = findOpeningAt(cm);
                if (hit) { e.preventDefault(); startOpeningDrag(hit.type, hit.index); return; }
              }
              if (tool === "pan") {
                panRef.current = { ...panRef.current, dragging: true, startX: tt.clientX, startY: tt.clientY, origX: panPx.x, origY: panPx.y };
              }
            }
          }}
          onTouchMove={(e) => {
            if (e.touches.length === 2 && canvasRef.current._pinch) {
              e.preventDefault();
              const [a, b] = e.touches;
              const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
              const cx = (a.clientX + b.clientX) / 2, cy = (a.clientY + b.clientY) / 2;
              const p = canvasRef.current._pinch;
              setZoomOverride(clampCmPx(p.cmpx * (d / p.d)));
              setPanPx({ x: p.px + (cx - p.cx), y: p.py + (cy - p.cy) });
            } else if (e.touches.length === 1 && panRef.current.dragging) {
              e.preventDefault();
              const tt = e.touches[0];
              setPanPx({ x: panRef.current.origX + (tt.clientX - panRef.current.startX), y: panRef.current.origY + (tt.clientY - panRef.current.startY) });
            }
          }}
          onTouchEnd={() => { canvasRef.current._pinch = null; panRef.current.dragging = false; clearInteracting(); }}
          onMouseMoveCapture={(e) => {
            if (panRef.current.dragging) {
              setPanPx({ x: panRef.current.origX + (e.clientX - panRef.current.startX), y: panRef.current.origY + (e.clientY - panRef.current.startY) });
            }
          }}
          className={`block w-full h-full touch-none ${tool === "text" ? "cursor-text" : tool === "eraser" ? (hovered ? "cursor-pointer" : "cursor-not-allowed") : tool === "pan" ? "cursor-grab" : "cursor-crosshair"}`}
        />
        {/* Floating length input while pending — DESKTOP ONLY on mobile the live cm on the rubber-band is enough */}
        {pending && tool === "wall" && (
          <div
            data-testid="guided-length-input"
            style={{ left: pending.x * cmPxRef.current + panPx.x + 12, top: pending.y * cmPxRef.current + panPx.y + 12 }}
            className="flex absolute z-10 bg-white border border-[#D06D4D] rounded-lg shadow-lg px-3 py-2 items-center gap-2"
          >
            <span className="text-xs uppercase tracking-widest text-stone-500">Lunghezza</span>
            <input
              ref={inputRef}
              type="number"
              inputMode="decimal"
              autoFocus
              value={lengthInput}
              onChange={(e) => setLengthInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); commitTypedLength(); }
                else if (e.key === "Escape") { setPending(null); setLengthInput(""); }
              }}
              placeholder="cm"
              className="bg-transparent outline-none text-sm text-stone-900 w-20 border-b border-stone-200 focus:border-[#D06D4D] py-0.5"
            />
            <span className="text-xs text-stone-500">cm</span>
            <button
              onClick={commitTypedLength}
              className="text-xs bg-[#D06D4D] text-white px-2.5 py-1 rounded-full hover:bg-[#b95a3d]"
            >OK</button>
          </div>
        )}
        {/* Clickable wall length labels (always in cm) */}
        {walls.map((w, i) => {
          const v = wallVec(w);
          const mid = wallPoint(w, 0.5);
          const left = mid.x * cmPxRef.current + panPx.x + 6;
          const top = mid.y * cmPxRef.current + panPx.y - 20;
          return (
            <button
              key={`walllen-${i}`}
              data-testid={`guided-wall-length-${i}`}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); setEditingWallLen(i); }}
              style={{ left, top }}
              className="absolute z-10 text-[11px] leading-none px-1.5 py-0.5 bg-white/85 hover:bg-[#D06D4D] hover:text-white text-stone-700 rounded border border-stone-200 hover:border-[#D06D4D] transition-colors"
            >{Math.round(v.len)} cm</button>
          );
        })}
        {/* Wall length edit popup — modifies second point along the wall's ORIGINAL axis */}
        {editingWallLen != null && walls[editingWallLen] && (() => {
          const w = walls[editingWallLen];
          const v = wallVec(w);
          const mid = wallPoint(w, 0.5);
          const left = mid.x * cmPxRef.current + panPx.x + 12;
          const top = mid.y * cmPxRef.current + panPx.y + 12;
          const currentLen = Math.round(v.len);
          const updateLen = (val) => {
            const num = parseFloat((val || "").toString().replace(",", "."));
            if (!isFinite(num) || num <= 0) return;
            setWalls((prev) => prev.map((wall, i) => {
              if (i !== editingWallLen) return wall;
              const wv = wallVec(wall);
              return { ...wall, x2: Math.round(wall.x1 + wv.ux * num), y2: Math.round(wall.y1 + wv.uy * num) };
            }));
          };
          return (
            <div data-testid="wall-length-popup" style={{ left, top }} className="absolute z-20 bg-white border border-[#D06D4D] rounded-lg shadow-lg px-3 py-2 flex items-center gap-2 pointer-events-none">
              <span className="text-xs uppercase tracking-widest text-stone-500 pointer-events-auto">Lunghezza</span>
              <input
                data-testid="wall-length-input"
                type="number" inputMode="decimal" min={1} step={1}
                defaultValue={currentLen}
                autoFocus
                onChange={(e) => updateLen(e.target.value)}
                onPointerDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setEditingWallLen(null); }}
                className="pointer-events-auto bg-transparent outline-none text-sm text-stone-900 w-20 border-b border-stone-200 focus:border-[#D06D4D] py-0.5"
              />
              <span className="text-xs text-stone-500 pointer-events-auto">cm</span>
              <button data-testid="wall-length-close" onClick={() => setEditingWallLen(null)} className="pointer-events-auto text-xs bg-stone-200 text-stone-900 p-1 rounded-full hover:bg-stone-300"><X size={12} /></button>
            </div>
          );
        })()}
        {selectedOpening && (() => {
          const arr = selectedOpening.kind === "door" ? doors : windows;
          const item = arr[selectedOpening.index];
          if (!item) return null;
          const w = walls[item.wallIndex]; if (!w) return null;
          const c = wallPoint(w, item.t);
          const left = c.x * cmPxRef.current + panPx.x + 12;
          const top = c.y * cmPxRef.current + panPx.y + 12;
          const label = selectedOpening.kind === "door" ? "Porta" : "Finestra";
          const updateWidth = (val) => {
            const num = parseFloat((val || "").toString().replace(",", "."));
            if (!isFinite(num) || num <= 0) return;
            const vlen = wallVec(w).len;
            const halfN = (num / 2) / vlen;
            const newT = Math.max(halfN, Math.min(1 - halfN, item.t));
            if (selectedOpening.kind === "door") setDoors((prev) => prev.map((d, i) => i === selectedOpening.index ? { ...d, width: num, t: newT } : d));
            else setWindows((prev) => prev.map((wn, i) => i === selectedOpening.index ? { ...wn, width: num, t: newT } : wn));
          };
          const deleteIt = () => {
            if (selectedOpening.kind === "door") {
              setHistory((h) => [...h, { kind: "erase", type: "door", item, index: selectedOpening.index }]);
              setDoors((prev) => prev.filter((_, i) => i !== selectedOpening.index));
            } else {
              setHistory((h) => [...h, { kind: "erase", type: "window", item, index: selectedOpening.index }]);
              setWindows((prev) => prev.filter((_, i) => i !== selectedOpening.index));
            }
            setSelectedOpening(null);
          };
          return (
            <div data-testid="opening-width-popup" style={{ left, top }} className="absolute z-20 bg-white border border-[#D06D4D] rounded-lg shadow-lg px-3 py-2 flex items-center gap-2 pointer-events-none">
              <span className="text-xs uppercase tracking-widest text-stone-500 pointer-events-auto">{label}</span>
              <input
                data-testid="opening-width-input"
                type="number"
                inputMode="decimal"
                min={10}
                step={1}
                value={item.width}
                onChange={(e) => updateWidth(e.target.value)}
                onPointerDown={(e) => e.stopPropagation()}
                className="pointer-events-auto bg-transparent outline-none text-sm text-stone-900 w-16 border-b border-stone-200 focus:border-[#D06D4D] py-0.5"
              />
              <span className="text-xs text-stone-500 pointer-events-auto">cm</span>
              <button data-testid="opening-delete" onClick={deleteIt} className="pointer-events-auto text-xs bg-stone-200 text-stone-900 p-1 rounded-full hover:bg-red-200 hover:text-red-800"><Trash2 size={12} /></button>
              <button data-testid="opening-close" onClick={() => setSelectedOpening(null)} className="pointer-events-auto text-xs bg-stone-200 text-stone-900 p-1 rounded-full hover:bg-stone-300"><X size={12} /></button>
            </div>
          );
        })()}
        {texts.map((tx) => (
          <div key={tx.id} style={{ left: tx.x * cmPxRef.current + panPx.x, top: tx.y * cmPxRef.current + panPx.y }} className="absolute group select-none" data-testid={`guided-text-${tx.id}`}>
            <div onPointerDown={(e) => startDrag(e, tx.id)} className="flex items-start gap-1 bg-white/95 border border-stone-300 rounded shadow-sm px-1.5 py-0.5 cursor-move hover:border-[#D06D4D] touch-none">
              <input type="text" value={tx.content} onChange={(e) => updateText(tx.id, e.target.value)} onPointerDown={(e) => e.stopPropagation()} className="bg-transparent outline-none text-sm text-stone-900 min-w-[60px]" style={{ width: `${Math.max(60, (tx.content?.length || 0) * 8 + 12)}px` }} />
              <button onPointerDown={(e) => e.stopPropagation()} onClick={() => deleteText(tx.id)} className="text-stone-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Elimina testo"><X size={12} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Client-side image resize (mobile photos are often 15-30MB) ----
async function resizePhoto(file, maxSide = 1800, quality = 0.85) {
  if (!file || !(file.type || "").startsWith("image/")) return file;
  // If HEIC/HEIF, browser can't draw it to canvas — send original (backend now accepts it)
  const type = (file.type || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  if (type.includes("heic") || type.includes("heif") || name.endsWith(".heic") || name.endsWith(".heif")) return file;
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const longest = Math.max(img.width, img.height);
        if (!longest) { URL.revokeObjectURL(url); resolve(file); return; }
        const ratio = Math.min(1, maxSide / longest);
        const w = Math.max(1, Math.round(img.width * ratio));
        const h = Math.max(1, Math.round(img.height * ratio));
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          if (!blob) { resolve(file); return; }
          // If compressed blob isn't smaller, keep original
          if (blob.size >= file.size) { resolve(file); return; }
          const outName = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
          resolve(new File([blob], outName, { type: "image/jpeg", lastModified: Date.now() }));
        }, "image/jpeg", quality);
      } catch (e) {
        URL.revokeObjectURL(url);
        reject(e);
      }
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

// ---- Contact form after drawing — 4-step wizard ----
function ContactForm({ initialData, projectName, onCancel, onSent }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    intervention_type: "restructure", room_type: "living", room_type_other: "",
    notes: projectName ? `${t("draw.wizard.project")}: ${projectName}` : "",
    name: "", email: "", phone: "",
    privacy: false,
    package: "planit_draw_base",
    quantity: 1,
    payment_method: "stripe",
    _hp: "",
  });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);

  const packages = [
    { key: "planit_draw_base", unitPrice: 34.90, name: t("draw.wizard.packages.base.name"), desc: t("draw.wizard.packages.base.desc"), img: "https://customer-assets-wrfwihn1.emergentagent.net/job_plan-it-draw/artifacts/tfr8q6kv_image.png" },
    { key: "planit_draw_detail", unitPrice: 69.90, name: t("draw.wizard.packages.detail.name"), desc: t("draw.wizard.packages.detail.desc"), img: "https://customer-assets-wrfwihn1.emergentagent.net/job_plan-it-draw/artifacts/bxdpfv3q_cucina%20caporicci%201.png" },
  ];
  const currentPkg = packages.find((p) => p.key === form.package) || packages[0];
  const fmtEUR = (n) => `€${n.toFixed(2).replace(".", ",")}`;
  const unitPriceStr = fmtEUR(currentPkg.unitPrice);
  const totalPrice = currentPkg.unitPrice * form.quantity;
  const totalStr = fmtEUR(totalPrice);

  const uploadPhoto = async (file) => {
    const resized = await resizePhoto(file).catch((e) => { console.warn("resize failed, sending original", e); return file; });
    const fd = new FormData();
    fd.append("file", resized);
    const { data } = await api.post("/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000,
    });
    return data.path;
  };

  const submitAndPay = async () => {
    setSubmitting(true);
    try {
      const sub = await ensureSubmission();
      if (!sub) return;
      if (form.payment_method === "stripe") {
        const { data: co } = await api.post("/payments/checkout", { lookup_key: form.package, planimetry_id: sub.id, quantity: form.quantity, origin_url: window.location.origin });
        window.location.href = co.checkout_url;
        return;
      }
      // Bonifico → mostra success con istruzioni bonifico
      toast.success("Progetto inviato — vedi istruzioni di pagamento");
      onSent({ payment_method: form.payment_method, package_name: currentPkg.name, price: totalStr, quantity: form.quantity, name: form.name });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally { setSubmitting(false); }
  };

  // Idempotent planimetry submission (photos + POST /planimetry). Reused by
  // Stripe/Bonifico submit button and by PayPal Buttons createOrder.
  const ensureSubmission = async () => {
    if (submissionId) return { id: submissionId };
    const photo_urls = [];
    for (const f of photos) {
      try {
        photo_urls.push(await uploadPhoto(f));
      } catch (err) {
        console.error("photo upload failed", f.name, err?.response?.status, err?.message);
        toast.error(`Foto "${f.name}" non caricata`);
      }
    }
    const { data: sub } = await api.post("/planimetry", {
      ...initialData,
      intervention_type: form.intervention_type,
      room_type: form.room_type === "other" ? `Altro: ${form.room_type_other}` : form.room_type,
      notes: form.notes,
      name: form.name, email: form.email, phone: form.phone,
      privacy_consent: form.privacy,
      package: form.package,
      quantity: form.quantity,
      payment_method: form.payment_method,
      photo_urls,
      _hp: form._hp,
    });
    clearDraft();
    setSubmissionId(sub.id);
    return sub;
  };

  const rooms = [
    { k: "living", l: t("draw.wizard.rooms.living") }, { k: "kitchen", l: t("draw.wizard.rooms.kitchen") }, { k: "bedroom", l: t("draw.wizard.rooms.bedroom") },
    { k: "kids", l: t("draw.wizard.rooms.kids") }, { k: "bathroom", l: t("draw.wizard.rooms.bathroom") }, { k: "office", l: t("draw.wizard.rooms.office") },
    { k: "entrance", l: t("draw.wizard.rooms.entrance") }, { k: "dining", l: t("draw.wizard.rooms.dining") }, { k: "other", l: t("draw.wizard.rooms.other") },
  ];

  const canNext = (
    (step === 1 && form.intervention_type && form.room_type && (form.room_type !== "other" || form.room_type_other.trim())) ||
    (step === 2 && form.name && form.email && form.phone && form.privacy) ||
    (step === 3 && form.package) ||
    (step === 4 && form.payment_method)
  );

  return (
    <div className="min-h-screen bg-[#F9F8F6] px-6 py-16">
      <div className="max-w-2xl mx-auto">
        <button data-testid="planform-cancel" onClick={onCancel} className="text-sm text-stone-600 hover:text-stone-900 inline-flex items-center gap-2">
          <ArrowLeft size={16} /> {t("draw.wizard.backToDrawing")}
        </button>
        <div className="eyebrow mt-8">{t("draw.wizard.stepOf", { n: step })}</div>
        <h1 className="font-serif text-4xl mt-4 text-stone-900" data-testid="planform-title">
          {step === 1 && t("draw.wizard.titles.s1")}
          {step === 2 && t("draw.wizard.titles.s2")}
          {step === 3 && t("draw.wizard.titles.s3")}
          {step === 4 && t("draw.wizard.titles.s4")}
        </h1>
        {projectName && <p className="mt-2 text-stone-500 text-sm">{t("draw.wizard.project")}: <span className="text-stone-800 font-medium">{projectName}</span></p>}

        <div className="mt-8 flex gap-2">
          {[1,2,3,4].map((s) => <div key={s} className={`h-1 flex-1 rounded ${s <= step ? "bg-[#D06D4D]" : "bg-stone-200"}`} />)}
        </div>

        <div className="mt-8 bg-white border border-stone-200 p-8 space-y-6">
          {step === 1 && (<>
            <div>
              <label className="block text-xs uppercase tracking-widest text-stone-600 mb-3">{t("draw.wizard.interventionType")}</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[{k:"restructure",l:t("draw.wizard.interventions.restructure")},{k:"furniture",l:t("draw.wizard.interventions.furniture")}].map((o) => (
                  <button key={o.k} type="button" data-testid={`planform-type-${o.k}`}
                    onClick={() => setForm({ ...form, intervention_type: o.k })}
                    className={`text-left px-4 py-3 rounded border text-sm ${form.intervention_type===o.k?"border-[#D06D4D] bg-[#D06D4D]/5 text-stone-900":"border-stone-300 text-stone-700"}`}>{o.l}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-stone-600 mb-2">{t("draw.wizard.roomType")}</label>
              <select data-testid="planform-room" value={form.room_type} onChange={(e) => setForm({ ...form, room_type: e.target.value })} className="w-full bg-transparent border-b border-stone-300 py-2 text-stone-900">
                {rooms.map((r) => <option key={r.k} value={r.k}>{r.l}</option>)}
              </select>
              {form.room_type === "other" && (
                <input data-testid="planform-room-other" placeholder={t("draw.wizard.roomOther")} value={form.room_type_other} onChange={(e) => setForm({ ...form, room_type_other: e.target.value })} className="mt-3 w-full bg-transparent border-b border-stone-300 focus:border-[#D06D4D] outline-none py-2 text-stone-900" />
              )}
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-stone-600 mb-2">{t("draw.wizard.notes")}</label>
              <textarea data-testid="planform-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-transparent border-b border-stone-300 focus:border-[#D06D4D] outline-none py-2 text-stone-900" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-stone-600 mb-2">{t("draw.wizard.photos")}</label>
              <input data-testid="planform-photos" type="file" accept="image/*,.heic,.heif" multiple onChange={(e) => setPhotos(Array.from(e.target.files))} className="text-sm text-stone-700" />
              {photos.length > 0 && <p className="mt-2 text-xs text-stone-500">{t("draw.wizard.photosSelected", { n: photos.length })} · le foto verranno ridotte automaticamente per un caricamento rapido</p>}
            </div>
          </>)}

          {step === 2 && (<>
            {["name","email","phone"].map((k) => (
              <div key={k}>
                <label className="block text-xs uppercase tracking-widest text-stone-600 mb-2">{t(`draw.wizard.${k}`)}</label>
                <input data-testid={`planform-${k}`} required type={k==="email"?"email":"text"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full bg-transparent border-b border-stone-300 focus:border-[#D06D4D] outline-none py-2 text-stone-900" />
              </div>
            ))}
            <label className="flex items-start gap-3 pt-2 cursor-pointer">
              <input data-testid="planform-privacy" type="checkbox" checked={form.privacy} onChange={(e) => setForm({ ...form, privacy: e.target.checked })} className="mt-1" />
              <span className="text-sm text-stone-700">{t("draw.wizard.privacy")}<a href="/legal/privacy" target="_blank" rel="noopener" className="underline">{t("draw.wizard.privacyLink")}</a>{t("draw.wizard.privacyRest")}</span>
            </label>
            {/* Honeypot: hidden from real users, filled only by bots */}
            <input
              type="text"
              name="_hp"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={form._hp}
              onChange={(e) => setForm({ ...form, _hp: e.target.value })}
              style={{ position: "absolute", left: "-9999px", top: "-9999px", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
            />
          </>)}

          {step === 3 && (<>
            <p className="text-sm text-stone-600">{t("draw.wizard.pitch")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 relative">
              {packages.map((p) => (
                <button key={p.key} type="button" data-testid={`planform-pkg-${p.key}`}
                  onClick={() => setForm({ ...form, package: p.key })}
                  className={`relative text-left rounded-2xl border p-5 transition hover:z-40 ${form.package===p.key?"border-[#D06D4D] bg-[#D06D4D]/5":"border-stone-300 hover:border-stone-500"}`}>
                  <div className="group aspect-[4/3] w-full bg-white rounded mb-4 border border-stone-200 flex items-center justify-center relative cursor-zoom-in">
                    <img src={p.img} alt={p.name} className="w-full h-full object-contain transition-transform duration-300 ease-out group-hover:scale-[2.2] group-hover:relative group-hover:z-40 group-hover:drop-shadow-2xl" loading="lazy" />
                    <div className="pointer-events-none absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-50">
                      <span className="bg-stone-900/80 text-white text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full">Zoom</span>
                    </div>
                  </div>
                  <div className="font-serif text-xl text-stone-900">{p.name}</div>
                  <div className="mt-1 text-[#D06D4D] font-semibold">{fmtEUR(p.unitPrice)} <span className="text-xs text-stone-500 font-normal">{t("draw.wizard.priceIncl")} · {t("draw.wizard.perRoom")}</span></div>
                  <p className="mt-3 text-sm text-stone-700">{p.desc}</p>
                </button>
              ))}
            </div>

            <div className="pt-4 border-t border-stone-200">
              <label className="block text-xs uppercase tracking-widest text-stone-600 mb-3">{t("draw.wizard.roomsCount")}</label>
              <div className="flex items-center gap-3">
                <button type="button" data-testid="planform-qty-dec" onClick={() => setForm({ ...form, quantity: Math.max(1, form.quantity - 1) })} className="w-10 h-10 rounded-full border border-stone-300 hover:bg-stone-100 text-lg font-semibold text-stone-700">−</button>
                <input data-testid="planform-qty" type="number" min="1" max="20" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Math.max(1, Math.min(20, parseInt(e.target.value) || 1)) })} className="w-16 text-center bg-transparent border-b border-stone-300 focus:border-[#D06D4D] outline-none py-2 text-stone-900 text-lg" />
                <button type="button" data-testid="planform-qty-inc" onClick={() => setForm({ ...form, quantity: Math.min(20, form.quantity + 1) })} className="w-10 h-10 rounded-full border border-stone-300 hover:bg-stone-100 text-lg font-semibold text-stone-700">+</button>
                <span className="text-xs text-stone-500 ml-2">{t("draw.wizard.roomsHint")}</span>
              </div>
              <div className="mt-4 flex items-baseline justify-between">
                <span className="text-sm text-stone-700">{t("draw.wizard.totalPreview")}</span>
                <span data-testid="planform-total-preview" className="text-2xl font-serif text-[#D06D4D]">{totalStr} <span className="text-xs text-stone-500 font-normal">{t("draw.wizard.priceIncl")}</span></span>
              </div>
            </div>
          </>)}

          {step === 4 && (<>
            <p className="text-sm text-stone-600">{t("draw.wizard.choosePay")}</p>
            <div className="space-y-3">
              {[
                { k: "stripe", l: t("draw.wizard.pay.stripe.l"), d: t("draw.wizard.pay.stripe.d"), disabled: false },
                { k: "bonifico", l: t("draw.wizard.pay.bonifico.l"), d: t("draw.wizard.pay.bonifico.d"), disabled: false },
                { k: "paypal", l: t("draw.wizard.pay.paypal.l"), d: t("draw.wizard.pay.paypal.d"), disabled: false },
              ].map((m) => (
                <label key={m.k} className={`flex items-start gap-3 p-4 rounded border transition ${m.disabled ? "opacity-50 cursor-not-allowed border-stone-200 bg-stone-50" : "cursor-pointer " + (form.payment_method===m.k?"border-[#D06D4D] bg-[#D06D4D]/5":"border-stone-300")}`}>
                  <input data-testid={`planform-pay-${m.k}`} type="radio" name="pay" disabled={m.disabled} checked={form.payment_method===m.k} onChange={() => !m.disabled && setForm({ ...form, payment_method: m.k })} className="mt-1" />
                  <div>
                    <div className="font-semibold text-stone-900">{m.l}</div>
                    <div className="text-xs text-stone-600 mt-0.5">{m.d}</div>
                  </div>
                </label>
              ))}
            </div>

            {form.payment_method === "bonifico" && (
              <div data-testid="planform-bonifico-details" className="mt-2 rounded-2xl border border-[#D06D4D]/30 bg-[#D06D4D]/5 p-5 text-sm space-y-2">
                <div className="font-serif text-lg text-stone-900">{t("draw.wizard.bonifico.title")}</div>
                <div className="grid grid-cols-1 gap-1 text-stone-800">
                  <div><span className="text-stone-500 text-xs uppercase tracking-wider">{t("draw.wizard.bonifico.holder")}</span><br/>{t("draw.wizard.bonifico.holderVal")}</div>
                  <div><span className="text-stone-500 text-xs uppercase tracking-wider">{t("draw.wizard.bonifico.bank")}</span><br/>{t("draw.wizard.bonifico.bankVal")}</div>
                  <div><span className="text-stone-500 text-xs uppercase tracking-wider">{t("draw.wizard.bonifico.iban")}</span><br/><span className="font-mono text-[15px]">IT61W0306921811100000006333</span></div>
                  <div><span className="text-stone-500 text-xs uppercase tracking-wider">{t("draw.wizard.bonifico.causal")}</span><br/>{t("draw.wizard.project")} {form.name || t("draw.wizard.bonifico.yourName")} — {currentPkg.name} × {form.quantity}</div>
                  <div><span className="text-stone-500 text-xs uppercase tracking-wider">{t("draw.wizard.bonifico.amount")}</span><br/><span className="text-[#D06D4D] font-semibold">{totalStr}</span> {t("draw.wizard.priceIncl")}</div>
                </div>
                <p className="text-xs text-stone-600 pt-2">{t("draw.wizard.bonifico.note")}</p>
              </div>
            )}

            <div className="pt-4 border-t border-stone-200 text-sm space-y-1">
              <div className="flex justify-between text-stone-700">
                <span>{currentPkg.name} × {form.quantity}</span>
                <span>{unitPriceStr} × {form.quantity}</span>
              </div>
              <div className="flex justify-between text-stone-900 font-semibold pt-1">
                <span>{t("draw.wizard.total")}</span>
                <span data-testid="planform-total">{totalStr}</span>
              </div>
              <div className="mt-1 text-xs text-stone-500">{t("draw.wizard.priceIncl")}</div>
            </div>
          </>)}

          <div className="flex justify-between pt-4">
            {step > 1 ? <button data-testid="planform-back" type="button" onClick={() => setStep(step-1)} className="text-sm text-stone-600 hover:text-stone-900">{t("draw.wizard.back")}</button> : <span />}
            {step < 4 ? (
              <button data-testid="planform-next" type="button" disabled={!canNext} onClick={() => setStep(step+1)} className="bg-[#D06D4D] hover:bg-[#b95a3d] disabled:opacity-50 text-white px-6 py-2 rounded-full text-sm font-semibold">{t("draw.wizard.next")}</button>
            ) : form.payment_method === "paypal" ? (
              <span />
            ) : (
              <button data-testid="planform-submit" type="button" disabled={!canNext || submitting} onClick={submitAndPay} className="bg-[#D06D4D] hover:bg-[#b95a3d] disabled:opacity-50 text-white px-6 py-2 rounded-full text-sm font-semibold">
                {submitting ? t("draw.wizard.sending") : (form.payment_method === "stripe" ? t("draw.wizard.submitPay") : t("draw.wizard.submitSend"))}
              </button>
            )}
          </div>

          {step === 4 && form.payment_method === "paypal" && (
            <div data-testid="planform-paypal-container" className="pt-4 border-t border-stone-200">
              {!canNext ? (
                <p className="text-sm text-stone-500">Completa i campi richiesti per procedere.</p>
              ) : (
                <PayPalScriptProvider options={{ clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID, currency: "EUR", intent: "capture", locale: "it_IT" }}>
                  <PayPalButtons
                    style={{ layout: "vertical", color: "gold", shape: "pill", label: "paypal", height: 45 }}
                    disabled={submitting}
                    forceReRender={[form.package, form.quantity, form.payment_method]}
                    createOrder={async () => {
                      setSubmitting(true);
                      try {
                        const sub = await ensureSubmission();
                        const { data } = await api.post("/paypal/create-order", {
                          lookup_key: form.package,
                          planimetry_id: sub.id,
                          quantity: form.quantity,
                        });
                        return data.id;
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Errore avvio pagamento PayPal");
                        throw err;
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                    onApprove={async (data) => {
                      setSubmitting(true);
                      try {
                        await api.post(`/paypal/capture-order/${data.orderID}`);
                        toast.success("Pagamento PayPal completato!");
                        onSent({ payment_method: "paypal", package_name: currentPkg.name, price: totalStr, quantity: form.quantity, name: form.name });
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Errore conferma pagamento PayPal");
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                    onError={(err) => {
                      console.error("PayPal error", err);
                      toast.error("Errore PayPal — riprova o scegli un altro metodo");
                    }}
                    onCancel={() => {
                      toast.info("Pagamento PayPal annullato");
                    }}
                  />
                </PayPalScriptProvider>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Payment result pages ----
export function PaymentSuccess() {
  const nav = useNavigate();
  const [status, setStatus] = useState("pending");
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) return;
    let attempts = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        setStatus(data.payment_status);
        if (data.payment_status === "paid" || attempts++ > 30) return;
      } catch (e) { console.warn("payment status poll failed", e); }
      setTimeout(tick, 2000);
    };
    tick();
  }, []);
  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-6">
      <div className="text-center max-w-xl">
        <div className="eyebrow">✓</div>
        <h1 className="font-serif text-4xl mt-4 text-stone-900" data-testid="payment-success-title">
          {status === "paid" ? "Pagamento ricevuto!" : "Verifica pagamento in corso…"}
        </h1>
        <p className="mt-4 text-stone-700">{status === "paid" ? "Ti abbiamo inviato una email di conferma. Ti risponderemo entro 2 giorni lavorativi con il tuo disegno tecnico." : "Un momento, stiamo confermando il pagamento con Stripe."}</p>
        <button onClick={() => nav("/")} className="mt-8 bg-[#D06D4D] hover:bg-[#b95a3d] text-white px-8 py-3 rounded-full text-sm font-semibold">Home</button>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-6">
      <div className="text-center max-w-xl">
        <h1 className="font-serif text-4xl text-stone-900" data-testid="payment-cancel-title">Pagamento annullato</h1>
        <p className="mt-4 text-stone-700">Nessun addebito è stato effettuato. Puoi riprovare quando vuoi.</p>
        <button onClick={() => nav("/draw")} className="mt-8 bg-[#D06D4D] hover:bg-[#b95a3d] text-white px-8 py-3 rounded-full text-sm font-semibold">Torna al disegno</button>
      </div>
    </div>
  );
}

export default function Draw() {
  const [mode, setMode] = useState(null);
  const [drawData, setDrawData] = useState(null);
  const [done, setDone] = useState(false);
  const [doneInfo, setDoneInfo] = useState(null);
  const [projectName, setProjectName] = useState("");
  const nav = useNavigate();
  const { t } = useTranslation();

  // Load saved projectName + detect existing draft
  useEffect(() => {
    const d = loadDraft();
    if (d?.projectName) setProjectName(d.projectName);
  }, []);
  const hasDraft = !!loadDraft();

  if (done) {
    const isBonifico = doneInfo?.payment_method === "bonifico";
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-6 py-16">
        <div className="max-w-xl w-full">
          <div className="text-center">
            <div className="eyebrow">✓</div>
            <h1 className="font-serif text-4xl mt-4 text-stone-900" data-testid="draw-success">
              {isBonifico ? t("draw.wizard.successBonifico") : t("draw.form.success")}
            </h1>
            {isBonifico && (
              <p className="mt-3 text-stone-700 text-sm">
                {t("draw.wizard.successBonificoSub")}
              </p>
            )}
          </div>

          {isBonifico && (
            <div data-testid="draw-success-bonifico" className="mt-8 rounded-2xl border border-[#D06D4D]/30 bg-white p-6 text-sm space-y-3 text-left">
              <div className="font-serif text-xl text-stone-900">{t("draw.wizard.bonifico.title")}</div>
              <div className="grid grid-cols-1 gap-3 text-stone-800">
                <div><span className="text-stone-500 text-xs uppercase tracking-wider">{t("draw.wizard.bonifico.holder")}</span><br/>{t("draw.wizard.bonifico.holderVal")}</div>
                <div><span className="text-stone-500 text-xs uppercase tracking-wider">{t("draw.wizard.bonifico.bank")}</span><br/>{t("draw.wizard.bonifico.bankVal")}</div>
                <div><span className="text-stone-500 text-xs uppercase tracking-wider">{t("draw.wizard.bonifico.iban")}</span><br/><span className="font-mono text-[15px] select-all">IT61W0306921811100000006333</span></div>
                <div><span className="text-stone-500 text-xs uppercase tracking-wider">{t("draw.wizard.bonifico.causal")}</span><br/>{t("draw.wizard.project")} {doneInfo?.name || ""} — {doneInfo?.package_name}{doneInfo?.quantity > 1 ? ` × ${doneInfo.quantity}` : ""}</div>
                <div><span className="text-stone-500 text-xs uppercase tracking-wider">{t("draw.wizard.bonifico.amount")}</span><br/><span className="text-[#D06D4D] font-semibold">{doneInfo?.price}</span> {t("draw.wizard.priceIncl")}</div>
              </div>
              <p className="text-xs text-stone-600 pt-2 border-t border-stone-100">{t("draw.wizard.bonifico.successNote")}</p>
            </div>
          )}

          <div className="text-center">
            <button onClick={() => nav("/")} data-testid="draw-home" className="mt-8 bg-[#D06D4D] hover:bg-[#b95a3d] text-white px-8 py-3 rounded-full text-sm font-semibold">{t("draw.wizard.home")}</button>
          </div>
        </div>
      </div>
    );
  }
  if (drawData) return <ContactForm initialData={drawData} projectName={projectName} onCancel={() => setDrawData(null)} onSent={(info) => { setDoneInfo(info || null); setDone(true); }} />;
  if (mode === "sketch") return <SketchCanvas onSubmit={setDrawData} projectName={projectName} />;
  // GUIDED mode temporarily disabled (2026-02-08). Redirect to picker if triggered.
  if (mode === "guided") { setTimeout(() => setMode(null), 0); return null; }
  return <ModePicker onPick={setMode} projectName={projectName} setProjectName={setProjectName} hasDraft={hasDraft} />;
}
